import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CommonService from '../../../services/common.service';
import { toast } from 'react-toastify';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import '../../../assets/styles/buyer_rfq_list.scss';

const getQuotationListStatus = (quoteStatus) => {
  const normalized = (quoteStatus || '').toString().trim().toLowerCase();
  if (!normalized || normalized === 'draft') return 'Draft';
  if (normalized === 'submitted') return 'Quoted';
  return (quoteStatus || 'Quoted').toString().trim();
};

const getQuotationStatusClass = (status) =>
  `brfq-status-${(status || 'quoted').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

export default function BuyerRFQList() {
  const navigate = useNavigate();
  const location = useLocation();
  // Initialize activeTab from location.state, sessionStorage, or default to 'request'
  const [activeTab, setActiveTab] = useState(() => {
    // Priority: 1. location.state, 2. sessionStorage, 3. default
    const tabFromState = location.state?.activeTab;
    const tabFromStorage = sessionStorage.getItem('activeTab');
    const initialTab = tabFromState || tabFromStorage || 'request';
    
    // Clear sessionStorage after reading to prevent persistence across sessions
    if (tabFromStorage) {
      sessionStorage.removeItem('activeTab');
    }
    
    console.log('Initializing activeTab:', {
      fromState: tabFromState,
      fromStorage: tabFromStorage,
      final: initialTab
    });
    
    return initialTab;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showClarificationModal, setShowClarificationModal] = useState(false);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  
  // Master Data Hooks
  const { currencyOptions, loading: currencyLoading } = useCurrencyMaster();
  const { attributeUnitOptions, loading: uomLoading } = useProductDetailsMaster();
  
  // Debug: Log when master data loads
  useEffect(() => {
    console.log('📊 Master Data Status:', {
      currencyLoading,
      currencyOptionsCount: currencyOptions?.length || 0,
      uomLoading,
      unitOptionsCount: attributeUnitOptions?.length || 0,
      sampleCurrencyOptions: currencyOptions?.slice(0, 3),
      sampleUnitOptions: attributeUnitOptions?.slice(0, 3)
    });
  }, [currencyLoading, uomLoading, currencyOptions, attributeUnitOptions]);
  
  // Helper function to get currency label from value (ID or code)
  const getCurrencyLabel = (currencyValue) => {
    if (!currencyValue) return 'INR';
    
    // First try to find by value (ID)
    const foundByValue = currencyOptions.find(opt => 
      String(opt.value) === String(currencyValue) || 
      opt.currencyCode === String(currencyValue)
    );
    if (foundByValue) {
      // Return currency code if available, otherwise label
      return foundByValue.currencyCode || foundByValue.label || currencyValue;
    }
    
    // If not found, it might already be a currency code (like "INR", "USD")
    // Check if it matches any currency code
    const foundByCode = currencyOptions.find(opt => 
      opt.currencyCode === String(currencyValue).toUpperCase()
    );
    if (foundByCode) {
      return foundByCode.currencyCode || foundByCode.label || currencyValue;
    }
    
    // If still not found, return the value as is (might already be a label)
    return currencyValue;
  };
  
  // Helper function to get currency symbol from currency code (for view-only displays)
  // Uses only API data - no hardcoded fallbacks
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '';
    
    // Get symbol from currencyOptions (API data only)
    if (currencyOptions && currencyOptions.length > 0) {
      const foundByCode = currencyOptions.find(opt => 
        opt.currencyCode === String(currencyCode).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        opt.currencyCode === String(currencyCode)
      );
      if (foundByCode && foundByCode.currencySymbol) {
        return foundByCode.currencySymbol;
      }
    }
    
    // Return empty string if no symbol found from API
    return '';
  };
  
  // Helper function to get unit label from value (ID or label)
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return '';
    
    // If master data not loaded yet, return the value as is
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) {
      console.log('getUnitLabel: Options not loaded, returning value as-is:', unitValue);
      return unitValue;
    }
    
    // First try to find by value (ID) - convert both to numbers for comparison
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find(opt => {
      const optValueNum = isNaN(opt.value) ? null : Number(opt.value);
      // Try exact string match first
      if (String(opt.value) === String(unitValue)) return true;
      // Try numeric match if both are numbers
      if (unitValueNum !== null && optValueNum !== null) {
        return optValueNum === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      console.log('getUnitLabel: Found by ID/value', unitValue, '->', foundByValue.label);
      return foundByValue.label || unitValue;
    }
    
    // If not found by ID, try to find by label (might already be a label)
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      console.log('getUnitLabel: Found by label', unitValue, '->', foundByLabel.label);
      return foundByLabel.label;
    }
    
    // If still not found, log warning and return the value as is
    console.warn('getUnitLabel: No match found for', unitValue, 'Available options:', attributeUnitOptions.length, 'Sample:', attributeUnitOptions.slice(0, 3));
    return unitValue;
  };
  
  // Helper function to transform raw RFQ data with currency and unit mapping
  const transformRFQData = (rfq) => {
    // Map currency ID to currency code and get symbol
    const currencyCode = getCurrencyLabel(rfq.target_price_currency || 'INR');
    const currencySymbol = getCurrencySymbol(currencyCode);
    
    // Format price range with currency
    const priceRange = rfq.target_price_min && rfq.target_price_max 
      ? `${currencySymbol}${rfq.target_price_min} - ${currencySymbol}${rfq.target_price_max}` 
      : rfq.target_price_currency ? 'Price on request' : 'N/A';
    
    // Map unit ID to unit label
    const unitLabel = getUnitLabel(rfq.quantity_unit || '');
    const quantityDisplay = `${rfq.product_quantity || 0} ${unitLabel}`.trim();
    
    return {
      id: `REQ-${rfq.rfq_id}`,
      rfq_id: rfq.rfq_id,
      status: 'Request',
      buyerName: rfq.buyer_name || rfq.company_name || 'Unknown Buyer',
      products: rfq.product_name || 'N/A',
      category: rfq.category_name || 'N/A',
      quantity: quantityDisplay,
      expectedPrice: priceRange,
      submittedOn: rfq.created_at ? new Date(rfq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A',
      requestedOn: rfq.created_at ? new Date(rfq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A',
      productName: rfq.product_name || '',
      productCategories: rfq.category_name ? [rfq.category_name] : [],
      targetPriceRange: priceRange,
      requiredQuantity: quantityDisplay,
      keywords: Array.isArray(rfq.keywords) ? rfq.keywords : [],
      procurementMethod: rfq.preferences || 'N/A',
      productDescription: rfq.product_description || '',
      productImages: Array.isArray(rfq.uploaded_files) ? rfq.uploaded_files : [],
      paymentTerms: rfq.payment_terms || '',
      incoterms: rfq.trade_terms || '',
      shippingAddress: rfq.shipping_address || '',
      // Use direct fields (now stored as names/strings) with fallback for backward compatibility
      shippingCity: rfq.shipping_city || rfq.shipping_state || rfq.shipping_country || rfq.shipping_city_name || rfq.shipping_state_name || rfq.shipping_country_name || '',
      preferredShippingMethod: rfq.preferred_shipping_method || '',
      expectedDelivery: rfq.lead_time_days ? `${rfq.lead_time_days} day(s) after initial payment` : '',
      specialRequirements: rfq.special_requirements || '',
      // Include all original RFQ data for reference
      originalData: rfq
    };
  };

  // Format currency function
  const formatCurrency = (amount, currencyCode = 'INR') => {
    // Get currency symbol for display
    const symbol = getCurrencySymbol(currencyCode);
    
    // Format number with proper locale
    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    
    // Return with currency symbol prefix
    return `${symbol}${formatted}`;
  };

  // Format procurement method - maps value to display label
  const formatProcurementMethod = (preferences) => {
    if (!preferences) return 'N/A';
    const procurementMethodMap = {
      'buy-with-help': "Buy with help from the platform's procurement team",
      'buy-directly': "Buy directly by posting my needs on the platform"
    };
    return procurementMethodMap[preferences] || preferences;
  };

  // Format rejection reason - maps value to display label
  const formatRejectionReason = (reason) => {
    if (!reason || reason === 'N/A') return 'N/A';
    const rejectionReasonMap = {
      'price-too-high': 'Price too high',
      'quality-issues': 'Quality issues',
      'delivery-time': 'Delivery time not acceptable',
      'terms-not-suitable': 'Terms not suitable',
      'other': 'Other'
    };
    return rejectionReasonMap[reason] || reason;
  };
  const [clarificationForm, setClarificationForm] = useState({
    subject: '',
    message: '',
    files: []
  });
  const clarificationFileInputRef = useRef(null);
  const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
  const [selectedRejectRFQ, setSelectedRejectRFQ] = useState(null);
  const [showViewRequestModal, setShowViewRequestModal] = useState(false);
  const [selectedViewRequestRFQ, setSelectedViewRequestRFQ] = useState(null);
  const [showViewQuotationModal, setShowViewQuotationModal] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showAcceptanceDetailsModal, setShowAcceptanceDetailsModal] = useState(false);
  const [selectedAcceptRFQ, setSelectedAcceptRFQ] = useState(null);

  // State for fetched RFQ data
  const [requestRFQs, setRequestRFQs] = useState([]);
  const [quotedRFQs, setQuotedRFQs] = useState([]);
  const [rejectedRFQs, setRejectedRFQs] = useState([]);
  const [acceptedRFQs, setAcceptedRFQs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quotedRFQsFetched, setQuotedRFQsFetched] = useState(false);
  const [rejectedRFQsFetched, setRejectedRFQsFetched] = useState(false);
  const [acceptedRFQsFetched, setAcceptedRFQsFetched] = useState(false);
  // Store raw RFQ data for re-transformation when master data loads
  const [rawRequestRFQs, setRawRequestRFQs] = useState([]);
  const [rawQuotedRFQs, setRawQuotedRFQs] = useState([]);
  const [rawRejectedRFQs, setRawRejectedRFQs] = useState([]);
  const [rawAcceptedRFQs, setRawAcceptedRFQs] = useState([]);

  // Set active tab from location state or sessionStorage if provided
  useEffect(() => {
    // Priority: location.state first, then sessionStorage
    if (location.state?.activeTab) {
      const newTab = location.state.activeTab;
      console.log('Setting activeTab from location.state:', newTab);
      // Only update if different to avoid unnecessary re-renders
      if (activeTab !== newTab) {
        setActiveTab(newTab);
      }
      // Clear sessionStorage if we got it from location.state
      sessionStorage.removeItem('activeTab');
    } else {
      const tabFromStorage = sessionStorage.getItem('activeTab');
      if (tabFromStorage && activeTab !== tabFromStorage) {
        console.log('Setting activeTab from sessionStorage:', tabFromStorage);
        setActiveTab(tabFromStorage);
        // Clear after reading
        sessionStorage.removeItem('activeTab');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Fetch quoted RFQs count on initial load (for tab count display)
  // This ensures the tab count is accurate even when request tab is active
  useEffect(() => {
    // Only fetch if we haven't fetched yet and we're not on the quoted tab
    // (if on quoted tab, the other useEffect will handle it)
    if (!quotedRFQsFetched && activeTab !== 'quoted') {
      // Fetch quoted RFQs on initial load to get accurate count for tab display
      const fetchData = async () => {
        await fetchQuotedRFQs();
        setQuotedRFQsFetched(true);
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // Run when activeTab changes or on mount

  // Fetch rejected RFQs count on initial load (for tab count display)
  // This ensures the tab count is accurate even when request tab is active
  useEffect(() => {
    // Only fetch if we haven't fetched yet and we're not on the rejected tab
    // (if on rejected tab, the other useEffect will handle it)
    if (!rejectedRFQsFetched && activeTab !== 'rejected') {
      // Fetch rejected RFQs on initial load to get accurate count for tab display
      const fetchData = async () => {
        await fetchRejectedRFQs();
        setRejectedRFQsFetched(true);
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // Run when activeTab changes or on mount

  // Fetch accepted RFQs count on initial load (for tab count display)
  // This ensures the tab count is accurate even when request tab is active
  useEffect(() => {
    // Only fetch if we haven't fetched yet and we're not on the accepted tab
    // (if on accepted tab, the other useEffect will handle it)
    if (!acceptedRFQsFetched && activeTab !== 'accepted') {
      // Fetch accepted RFQs on initial load to get accurate count for tab display
      const fetchData = async () => {
        await fetchAcceptedRFQs();
        setAcceptedRFQsFetched(true);
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // Run when activeTab changes or on mount

  // Fetch RFQ data from API based on active tab
  // Handle location.state changes (e.g., when navigating from quotation submission)
  useEffect(() => {
    if (location.state?.activeTab && location.state.activeTab !== activeTab) {
      console.log('Location state changed, updating active tab to:', location.state.activeTab);
      setActiveTab(location.state.activeTab);
      // Clear location state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state, activeTab]);

  useEffect(() => {
    console.log('Active tab changed to:', activeTab);
    if (activeTab === 'request') {
      fetchRequestRFQs();
    } else if (activeTab === 'quoted') {
      // Mark as fetched when quoted tab is active
      setQuotedRFQsFetched(true);
      // Always fetch quoted RFQs when quoted tab is active
      console.log('Fetching quoted RFQs for quoted tab...');
      fetchQuotedRFQs();
    } else if (activeTab === 'rejected') {
      // Mark as fetched when rejected tab is active
      setRejectedRFQsFetched(true);
      // Always fetch rejected RFQs when rejected tab is active
      console.log('Fetching rejected RFQs for rejected tab...');
      fetchRejectedRFQs();
    } else if (activeTab === 'accepted') {
      // Mark as fetched when accepted tab is active
      setAcceptedRFQsFetched(true);
      // Always fetch accepted RFQs when accepted tab is active
      console.log('Fetching accepted RFQs for accepted tab...');
      fetchAcceptedRFQs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Helper function to get RFQ IDs that have quotations from this seller
  // Returns an array of RFQ IDs that the seller has already quoted
  const getQuotedRFQIds = async () => {
    try {
      const response = await CommonService.get('/quotation/seller/my-quotations');
      if (response && response.success && response.data) {
        const quotations = Array.isArray(response.data) ? response.data : [];
        // Convert all RFQ IDs to numbers for consistent comparison
        return quotations
          .map(q => q.rfq_id)
          .filter(id => id != null)
          .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
          .filter(id => !isNaN(id));
      }
      return [];
    } catch (error) {
      console.error('Error fetching quoted RFQ IDs:', error);
      
      // Handle 401 Unauthorized - redirect to login
      if (error?.response?.status === 401) {
        toast.error('Your session has expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      }
      
      return [];
    }
  };

  // Fetch RFQs for Request tab
  // Displays: RFQs that match seller's product categories AND are NOT yet quoted by this seller
  const fetchRequestRFQs = async () => {
    try {
      setLoading(true);
      // Backend endpoint /rfq already filters RFQs by seller's product categories
      const response = await CommonService.get('/rfq');
      
      if (response && response.success && response.data) {
        const rfqs = Array.isArray(response.data) ? response.data : [];
        
        // Get RFQ IDs that have quotations from this seller
        const quotedRFQIds = await getQuotedRFQIds();
        
        // Convert RFQ IDs to numbers for consistent comparison
        const quotedRFQIdsSet = new Set(quotedRFQIds);
        
        // Filter out RFQs that already have quotations from this seller
        // This ensures Request tab only shows unquoted RFQs matching seller's categories
        // Convert rfq.rfq_id to number for comparison
        const unquotedRFQs = rfqs.filter(rfq => {
          const rfqId = typeof rfq.rfq_id === 'string' ? parseInt(rfq.rfq_id, 10) : Number(rfq.rfq_id);
          return !isNaN(rfqId) && !quotedRFQIdsSet.has(rfqId);
        });
        
        // Debug logging
        console.log('Quoted RFQ IDs:', quotedRFQIds);
        console.log('Total RFQs:', rfqs.length);
        console.log('Unquoted RFQs:', unquotedRFQs.length);
        
        // Store raw RFQ data
        setRawRequestRFQs(unquotedRFQs);
        
        // Only transform if master data is already loaded, otherwise wait for re-transformation
        const currencyLoaded = !currencyLoading && currencyOptions && currencyOptions.length > 0;
        const unitLoaded = !uomLoading && attributeUnitOptions && attributeUnitOptions.length > 0;
        
        if (currencyLoaded && unitLoaded) {
        // Transform API data to match the expected format
          const transformedRFQs = unquotedRFQs.map(rfq => transformRFQData(rfq));
          setRequestRFQs(transformedRFQs);
        } else {
          // Master data not loaded yet, set empty array - will be populated by re-transformation useEffect
          setRequestRFQs([]);
        }
      } else {
        setRequestRFQs([]);
      }
    } catch (error) {
      console.error('Error fetching RFQ requests:', error);
      
      // Handle 401 Unauthorized - but don't redirect here, let the API interceptor handle it
      // The interceptor will redirect, so we just need to handle the error gracefully
      if (error?.response?.status === 401) {
        // Don't redirect here - the API interceptor will handle it
        // Just set empty state and stop loading
        setRequestRFQs([]);
        setLoading(false);
        return;
      }
      
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch RFQ requests';
      // Only show error toast if it's not a 401 (interceptor will handle 401)
      if (error?.response?.status !== 401) {
        toast.error(errorMessage);
      }
      setRequestRFQs([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch RFQs for Quoted tab
  // Displays: Only RFQs that have already been quoted by this seller
  const fetchQuotedRFQs = async () => {
    try {
      console.log('Fetching quoted RFQs...');
      setLoading(true);
      // Fetch all quotations submitted by this seller
      const response = await CommonService.get('/quotation/seller/my-quotations');
      
      if (response && response.success && response.data) {
        const allQuotations = Array.isArray(response.data) ? response.data : [];
        
        // Filter out accepted and rejected quotations - only show pending/quoted/submitted status
        const quotations = allQuotations.filter(q => {
          const status = (q.quote_status || q.status || '').toString().toLowerCase();
          // Include quotations with status: submitted, quoted, pending, draft
          // Exclude: accepted, rejected
          const shouldInclude = status !== 'accepted' && status !== 'rejected' && status !== '';
          
          // Debug logging for first few quotations
          if (allQuotations.indexOf(q) < 3) {
            console.log(`Quotation ${q.quotation_id || q.id}: status="${status}" (quote_status="${q.quote_status}", status="${q.status}"), include=${shouldInclude}`);
          }
          
          return shouldInclude;
        });
        
        console.log(`Filtered quotations: ${quotations.length} (excluded ${allQuotations.length - quotations.length} accepted/rejected)`);
        console.log('Status breakdown:', {
          total: allQuotations.length,
          included: quotations.length,
          excluded: allQuotations.length - quotations.length,
          statuses: allQuotations.reduce((acc, q) => {
            const status = (q.quote_status || q.status || 'unknown').toString().toLowerCase();
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          }, {})
        });
        
        // Get unique RFQ IDs from quotations (seller may have multiple quotations for same RFQ)
        // Convert all RFQ IDs to numbers for consistent comparison
        const rfqIds = [...new Set(
          quotations
            .map(q => q.rfq_id)
            .filter(id => id != null)
            .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
            .filter(id => !isNaN(id))
        )];
        
        if (rfqIds.length === 0) {
          console.log('No quoted RFQs found');
          setQuotedRFQs([]);
          setRawQuotedRFQs([]);
          setLoading(false);
          return;
        }
        
        console.log(`Found ${rfqIds.length} quoted RFQ IDs:`, rfqIds);
        
        // Fetch RFQ details for each quoted RFQ
        const rfqPromises = rfqIds.map(async (rfqId) => {
          try {
            const rfqResponse = await CommonService.get(`/rfq/${rfqId}`);
            if (rfqResponse && rfqResponse.success && rfqResponse.data) {
              const rfq = rfqResponse.data;
              
              // Debug: Log RFQ data to check if category_name is present
              if (!rfq.category_name && rfq.category_id) {
                console.warn(`⚠️ RFQ ${rfqId} missing category_name, has category_id:`, rfq.category_id);
              }
              // Get the quotation for this RFQ - compare as numbers
              const rfqIdNum = typeof rfqId === 'string' ? parseInt(rfqId, 10) : Number(rfqId);
              const quotation = quotations.find(q => {
                const qRfqId = typeof q.rfq_id === 'string' ? parseInt(q.rfq_id, 10) : Number(q.rfq_id);
                return qRfqId === rfqIdNum;
              });
              
              // Map currency ID to currency code and get symbol
              const currencyCode = getCurrencyLabel(rfq.target_price_currency || 'INR');
              const currencySymbol = getCurrencySymbol(currencyCode);
              
          // Format price range with currency
          const priceRange = rfq.target_price_min && rfq.target_price_max 
            ? `${currencySymbol}${rfq.target_price_min} - ${currencySymbol}${rfq.target_price_max}` 
            : rfq.target_price_currency ? 'Price on request' : 'N/A';
          
              // Calculate quoted price from line items
              const quotedPrice = quotation && quotation.items && quotation.items.length > 0
                ? quotation.items.reduce((sum, item) => {
                    const unitPrice = parseFloat(item.unit_price) || 0;
                    const quantity = parseFloat(item.quantity) || 0;
                    const discount = parseFloat(item.discount) || 0;
                    const subtotal = unitPrice * quantity;
                    const discountAmount = subtotal * (discount / 100);
                    const fobValue = subtotal - discountAmount;
                    return sum + fobValue;
                  }, 0)
                : 0;
              
              // Get quotation currency (default to INR if not available)
              const quotationCurrency = quotation?.currency || rfq.target_price_currency || 'INR';
              const quotationCurrencyCode = getCurrencyLabel(quotationCurrency);
          
          return {
            id: `REQ-${rfq.rfq_id}`,
            rfq_id: rfq.rfq_id,
            status: getQuotationListStatus(quotation?.quote_status),
            quoteStatus: quotation?.quote_status || '',
            quotationDbId: quotation?.quotation_id,
            isDraft: (quotation?.quote_status || '').toString().toLowerCase() === 'draft',
            buyerName: rfq.buyer_name || rfq.company_name || 'Unknown Buyer',
            products: rfq.product_name || 'N/A',
            category: rfq.category_name || 'N/A',
                quantity: `${rfq.product_quantity || 0} ${getUnitLabel(rfq.quantity_unit || '')}`.trim(),
            expectedPrice: priceRange,
                quotedPrice: quotedPrice > 0 ? formatCurrency(quotedPrice, quotationCurrencyCode) : 'N/A',
                submittedOn: quotation && quotation.created_at 
                  ? new Date(quotation.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : 'N/A',
            requestedOn: rfq.created_at ? new Date(rfq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A',
            productName: rfq.product_name || '',
            productCategories: rfq.category_name ? [rfq.category_name] : [],
            targetPriceRange: priceRange,
                requiredQuantity: `${rfq.product_quantity || 0} ${getUnitLabel(rfq.quantity_unit || '')}`.trim(),
            keywords: Array.isArray(rfq.keywords) ? rfq.keywords : [],
            procurementMethod: rfq.preferences || 'N/A',
            productDescription: rfq.product_description || '',
            productImages: Array.isArray(rfq.uploaded_files) ? rfq.uploaded_files : [],
            paymentTerms: rfq.payment_terms || '',
            incoterms: rfq.trade_terms || '',
            shippingAddress: rfq.shipping_address || '',
            shippingCity: rfq.shipping_city || rfq.shipping_state || rfq.shipping_country || rfq.shipping_city_name || rfq.shipping_state_name || rfq.shipping_country_name || '',
            preferredShippingMethod: rfq.preferred_shipping_method || '',
            expectedDelivery: rfq.lead_time_days ? `${rfq.lead_time_days} day(s) after initial payment` : '',
            specialRequirements: rfq.special_requirements || '',
                originalData: rfq,
                quotationData: quotation
              };
            }
            return null;
          } catch (error) {
            console.error(`Error fetching RFQ ${rfqId}:`, error);
            return null;
          }
        });
        
        const quotedRFQsData = (await Promise.all(rfqPromises)).filter(rfq => rfq != null);
        console.log(`✅ Successfully fetched ${quotedRFQsData.length} quoted RFQs:`, quotedRFQsData);
        console.log('📊 Quoted RFQs data sample:', quotedRFQsData.length > 0 ? quotedRFQsData[0] : 'No data');
        
        // Store raw RFQ data for re-transformation
        const rawQuotedData = quotedRFQsData.map(item => item.originalData).filter(Boolean);
        setRawQuotedRFQs(rawQuotedData);
        
        console.log('🔄 Setting quotedRFQs state with', quotedRFQsData.length, 'items');
        setQuotedRFQs(quotedRFQsData);
      } else {
        console.log('No quotations found in response');
        setQuotedRFQs([]);
        setRawQuotedRFQs([]);
      }
    } catch (error) {
      console.error('Error fetching quoted RFQs:', error);
      
      // Handle 401 Unauthorized - but don't redirect here, let the API interceptor handle it
      // The interceptor will redirect, so we just need to handle the error gracefully
      if (error?.response?.status === 401) {
        // Don't redirect here - the API interceptor will handle it
        // Just set empty state and stop loading
        setQuotedRFQs([]);
        setRawQuotedRFQs([]);
        setLoading(false);
        return;
      }
      
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch quoted RFQs';
      // Only show error toast if we're on the quoted tab and it's not a 401
      if (activeTab === 'quoted' && error?.response?.status !== 401) {
      toast.error(errorMessage);
      }
      setQuotedRFQs([]);
      setRawQuotedRFQs([]);
    } finally {
      // Always set loading to false when fetch completes, regardless of active tab
      setLoading(false);
    }
  };

  // Fetch RFQs for Rejected tab
  // Displays: Only RFQs that have been rejected by the buyer
  const fetchRejectedRFQs = async () => {
    try {
      console.log('Fetching rejected RFQs...');
      setLoading(true);
      // Fetch all quotations submitted by this seller
      const response = await CommonService.get('/quotation/seller/my-quotations');
      
      if (response && response.success && response.data) {
        const quotations = Array.isArray(response.data) ? response.data : [];
        
        // Filter only rejected quotations
        const rejectedQuotations = quotations.filter(q => 
          q.quote_status && q.quote_status.toLowerCase() === 'rejected'
        );
        
        if (rejectedQuotations.length === 0) {
          console.log('No rejected RFQs found');
          setRejectedRFQs([]);
          setRawRejectedRFQs([]);
          setLoading(false);
          return;
        }
        
        // Get unique RFQ IDs from rejected quotations
        const rfqIds = [...new Set(
          rejectedQuotations
            .map(q => q.rfq_id)
            .filter(id => id != null)
            .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
            .filter(id => !isNaN(id))
        )];
        
        console.log(`Found ${rfqIds.length} rejected RFQ IDs:`, rfqIds);
        
        // Fetch RFQ details for each rejected RFQ
        const rfqPromises = rfqIds.map(async (rfqId) => {
          try {
            const rfqResponse = await CommonService.get(`/rfq/${rfqId}`);
            if (rfqResponse && rfqResponse.success && rfqResponse.data) {
              const rfq = rfqResponse.data;
              
              // Get the rejected quotation for this RFQ - compare as numbers
              const rfqIdNum = typeof rfqId === 'string' ? parseInt(rfqId, 10) : Number(rfqId);
              const quotation = rejectedQuotations.find(q => {
                const qRfqId = typeof q.rfq_id === 'string' ? parseInt(q.rfq_id, 10) : Number(q.rfq_id);
                return qRfqId === rfqIdNum;
              });
              
              // Map currency ID to currency code and get symbol
              const currencyCode = getCurrencyLabel(rfq.target_price_currency || 'INR');
              const currencySymbol = getCurrencySymbol(currencyCode);
              
              // Format price range with currency
              const priceRange = rfq.target_price_min && rfq.target_price_max 
                ? `${currencySymbol}${rfq.target_price_min} - ${currencySymbol}${rfq.target_price_max}` 
                : rfq.target_price_currency ? 'Price on request' : 'N/A';
              
              // Calculate quoted price from line items
              const quotedPrice = quotation && quotation.items && quotation.items.length > 0
                ? quotation.items.reduce((sum, item) => {
                    const unitPrice = parseFloat(item.unit_price) || 0;
                    const quantity = parseFloat(item.quantity) || 0;
                    const discount = parseFloat(item.discount) || 0;
                    const subtotal = unitPrice * quantity;
                    const discountAmount = subtotal * (discount / 100);
                    const fobValue = subtotal - discountAmount;
                    return sum + fobValue;
                  }, 0)
                : 0;
              
              // Get quotation currency (default to INR if not available)
              const quotationCurrency = quotation?.currency || rfq.target_price_currency || 'INR';
              const quotationCurrencyCode = getCurrencyLabel(quotationCurrency);
              
              // Format rejection attachment
              let rejectAttachments = [];
              if (quotation?.rejection_attachment) {
                const attachmentPath = quotation.rejection_attachment;
                const fileName = attachmentPath.split('/').pop() || 'Rejection_Attachment';
                const fileExtension = fileName.split('.').pop()?.toUpperCase() || 'FILE';
                rejectAttachments = [{
                  name: fileName,
                  url: attachmentPath.startsWith('http') ? attachmentPath : `http://localhost:3000${attachmentPath.startsWith('/') ? attachmentPath : '/' + attachmentPath}`,
                  type: fileExtension,
                  size: 'N/A',
                  uploadedDate: quotation.rejected_at ? new Date(quotation.rejected_at).toISOString().split('T')[0] : 'N/A'
                }];
              }
              
              return {
                id: `REQ-${rfq.rfq_id}`,
                rfq_id: rfq.rfq_id,
        status: 'Rejected',
                buyerName: rfq.buyer_name || rfq.company_name || 'Unknown Buyer',
                products: rfq.product_name || 'N/A',
                category: rfq.category_name || 'N/A',
                quantity: `${rfq.product_quantity || 0} ${getUnitLabel(rfq.quantity_unit || '')}`.trim(),
                expectedPrice: priceRange,
                quotedPrice: quotedPrice > 0 ? formatCurrency(quotedPrice, quotationCurrencyCode) : 'N/A',
                submittedOn: quotation && quotation.created_at 
                  ? new Date(quotation.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : 'N/A',
                rejectedOn: quotation && quotation.rejected_at 
                  ? new Date(quotation.rejected_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : 'N/A',
                rejectReason: quotation?.rejection_reason || 'N/A',
                rejectComment: quotation?.rejection_remarks || 'N/A',
                rejectAttachments: rejectAttachments,
                requestedOn: rfq.created_at ? new Date(rfq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A',
                productName: rfq.product_name || '',
                productCategories: rfq.category_name ? [rfq.category_name] : [],
                targetPriceRange: priceRange,
                requiredQuantity: `${rfq.product_quantity || 0} ${getUnitLabel(rfq.quantity_unit || '')}`.trim(),
                keywords: Array.isArray(rfq.keywords) ? rfq.keywords : [],
                procurementMethod: rfq.preferences || 'N/A',
                productDescription: rfq.product_description || '',
                productImages: Array.isArray(rfq.uploaded_files) ? rfq.uploaded_files : [],
                paymentTerms: rfq.payment_terms || '',
                incoterms: rfq.trade_terms || '',
                shippingAddress: rfq.shipping_address || '',
                shippingCity: rfq.shipping_city || rfq.shipping_state || rfq.shipping_country || rfq.shipping_city_name || rfq.shipping_state_name || rfq.shipping_country_name || '',
                preferredShippingMethod: rfq.preferred_shipping_method || '',
                expectedDelivery: rfq.lead_time_days ? `${rfq.lead_time_days} day(s) after initial payment` : '',
                specialRequirements: rfq.special_requirements || '',
                originalData: rfq,
                quotationData: quotation
              };
            }
            return null;
          } catch (error) {
            console.error(`Error fetching RFQ ${rfqId}:`, error);
            return null;
          }
        });
        
        const rejectedRFQsData = (await Promise.all(rfqPromises)).filter(rfq => rfq != null);
        console.log(`✅ Successfully fetched ${rejectedRFQsData.length} rejected RFQs:`, rejectedRFQsData);
        console.log('📊 Rejected RFQs data sample:', rejectedRFQsData.length > 0 ? rejectedRFQsData[0] : 'No data');
        
        // Store raw RFQ data for re-transformation
        const rawRejectedData = rejectedRFQsData.map(item => item.originalData).filter(Boolean);
        setRawRejectedRFQs(rawRejectedData);
        
        console.log('🔄 Setting rejectedRFQs state with', rejectedRFQsData.length, 'items');
        setRejectedRFQs(rejectedRFQsData);
      } else {
        console.log('No quotations found in response');
        setRejectedRFQs([]);
        setRawRejectedRFQs([]);
      }
    } catch (error) {
      console.error('Error fetching rejected RFQs:', error);
      
      // Handle 401 Unauthorized - but don't redirect here, let the API interceptor handle it
      if (error?.response?.status === 401) {
        // Don't redirect here - the API interceptor will handle it
        // Just set empty state and stop loading
        setRejectedRFQs([]);
        setRawRejectedRFQs([]);
        setLoading(false);
        return;
      }
      
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch rejected RFQs';
      // Only show error toast if we're on the rejected tab and it's not a 401
      if (activeTab === 'rejected' && error?.response?.status !== 401) {
        toast.error(errorMessage);
      }
      setRejectedRFQs([]);
      setRawRejectedRFQs([]);
    } finally {
      // Always set loading to false when fetch completes, regardless of active tab
      setLoading(false);
    }
  };

  // Fetch RFQs for Accepted tab
  // Displays: Only RFQs that have been accepted by the buyer
  const fetchAcceptedRFQs = async () => {
    try {
      console.log('Fetching accepted RFQs...');
      setLoading(true);
      // Fetch all quotations submitted by this seller
      const response = await CommonService.get('/quotation/seller/my-quotations');
      
      if (response && response.success && response.data) {
        const quotations = Array.isArray(response.data) ? response.data : [];
        
        // Filter only accepted quotations
        const acceptedQuotations = quotations.filter(q => 
          q.quote_status && q.quote_status.toLowerCase() === 'accepted'
        );
        
        if (acceptedQuotations.length === 0) {
          console.log('No accepted RFQs found');
          setAcceptedRFQs([]);
          setRawAcceptedRFQs([]);
          setLoading(false);
          return;
        }
        
        // Get unique RFQ IDs from accepted quotations
        const rfqIds = [...new Set(
          acceptedQuotations
            .map(q => q.rfq_id)
            .filter(id => id != null)
            .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
            .filter(id => !isNaN(id))
        )];
        
        console.log(`Found ${rfqIds.length} accepted RFQ IDs:`, rfqIds);
        
        // Fetch RFQ details for each accepted RFQ
        const rfqPromises = rfqIds.map(async (rfqId) => {
          try {
            const rfqResponse = await CommonService.get(`/rfq/${rfqId}`);
            if (rfqResponse && rfqResponse.success && rfqResponse.data) {
              const rfq = rfqResponse.data;
              
              // Get the accepted quotation for this RFQ - compare as numbers
              const rfqIdNum = typeof rfqId === 'string' ? parseInt(rfqId, 10) : Number(rfqId);
              const quotation = acceptedQuotations.find(q => {
                const qRfqId = typeof q.rfq_id === 'string' ? parseInt(q.rfq_id, 10) : Number(q.rfq_id);
                return qRfqId === rfqIdNum;
              });
              
              // Map currency ID to currency code and get symbol
              const currencyCode = getCurrencyLabel(rfq.target_price_currency || 'INR');
              const currencySymbol = getCurrencySymbol(currencyCode);
              
              // Format price range with currency
              const priceRange = rfq.target_price_min && rfq.target_price_max 
                ? `${currencySymbol}${rfq.target_price_min} - ${currencySymbol}${rfq.target_price_max}` 
                : rfq.target_price_currency ? 'Price on request' : 'N/A';
              
              // Calculate quoted price from line items
              const quotedPrice = quotation && quotation.items && quotation.items.length > 0
                ? quotation.items.reduce((sum, item) => {
                    const unitPrice = parseFloat(item.unit_price) || 0;
                    const quantity = parseFloat(item.quantity) || 0;
                    const discount = parseFloat(item.discount) || 0;
                    const subtotal = unitPrice * quantity;
                    const discountAmount = subtotal * (discount / 100);
                    const fobValue = subtotal - discountAmount;
                    return sum + fobValue;
                  }, 0)
                : 0;
              
              // Get quotation currency (default to INR if not available)
              const quotationCurrency = quotation?.currency || rfq.target_price_currency || 'INR';
              const quotationCurrencyCode = getCurrencyLabel(quotationCurrency);
              
              return {
                id: `REQ-${rfq.rfq_id}`,
                rfq_id: rfq.rfq_id,
                status: 'Accepted',
                buyerName: rfq.buyer_name || rfq.company_name || 'Unknown Buyer',
                products: rfq.product_name || 'N/A',
                category: rfq.category_name || 'N/A',
                quantity: `${rfq.product_quantity || 0} ${getUnitLabel(rfq.quantity_unit || '')}`.trim(),
                expectedPrice: priceRange,
                quotedPrice: quotedPrice > 0 ? formatCurrency(quotedPrice, quotationCurrencyCode) : 'N/A',
                submittedOn: quotation && quotation.created_at 
                  ? new Date(quotation.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : 'N/A',
                acceptedOn: quotation && quotation.accepted_at 
                  ? new Date(quotation.accepted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : 'N/A',
                acceptanceNotes: quotation?.acceptance_notes || null,
                acceptanceAttachment: quotation?.acceptance_attachment || null,
                requestedOn: rfq.created_at ? new Date(rfq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A',
                productName: rfq.product_name || '',
                productCategories: rfq.category_name ? [rfq.category_name] : [],
                targetPriceRange: priceRange,
                requiredQuantity: `${rfq.product_quantity || 0} ${getUnitLabel(rfq.quantity_unit || '')}`.trim(),
                keywords: Array.isArray(rfq.keywords) ? rfq.keywords : [],
                procurementMethod: rfq.preferences || 'N/A',
                productDescription: rfq.product_description || '',
                productImages: Array.isArray(rfq.uploaded_files) ? rfq.uploaded_files : [],
                paymentTerms: rfq.payment_terms || '',
                incoterms: rfq.trade_terms || '',
                shippingAddress: rfq.shipping_address || '',
                shippingCity: rfq.shipping_city || rfq.shipping_state || rfq.shipping_country || rfq.shipping_city_name || rfq.shipping_state_name || rfq.shipping_country_name || '',
                preferredShippingMethod: rfq.preferred_shipping_method || '',
                expectedDelivery: rfq.lead_time_days ? `${rfq.lead_time_days} day(s) after initial payment` : '',
                specialRequirements: rfq.special_requirements || '',
                originalData: rfq,
                quotationData: quotation
              };
            }
            return null;
          } catch (error) {
            console.error(`Error fetching RFQ ${rfqId}:`, error);
            return null;
          }
        });
        
        const acceptedRFQsData = (await Promise.all(rfqPromises)).filter(rfq => rfq != null);
        console.log(`✅ Successfully fetched ${acceptedRFQsData.length} accepted RFQs:`, acceptedRFQsData);
        console.log('📊 Accepted RFQs data sample:', acceptedRFQsData.length > 0 ? acceptedRFQsData[0] : 'No data');
        
        // Store raw RFQ data for re-transformation
        const rawAcceptedData = acceptedRFQsData.map(item => item.originalData).filter(Boolean);
        setRawAcceptedRFQs(rawAcceptedData);
        
        console.log('🔄 Setting acceptedRFQs state with', acceptedRFQsData.length, 'items');
        setAcceptedRFQs(acceptedRFQsData);
      } else {
        console.log('No quotations found in response');
        setAcceptedRFQs([]);
        setRawAcceptedRFQs([]);
      }
    } catch (error) {
      console.error('Error fetching accepted RFQs:', error);
      
      // Handle 401 Unauthorized - but don't redirect here, let the API interceptor handle it
      if (error?.response?.status === 401) {
        // Don't redirect here - the API interceptor will handle it
        // Just set empty state and stop loading
        setAcceptedRFQs([]);
        setRawAcceptedRFQs([]);
        setLoading(false);
        return;
      }
      
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch accepted RFQs';
      // Only show error toast if we're on the accepted tab and it's not a 401
      if (activeTab === 'accepted' && error?.response?.status !== 401) {
        toast.error(errorMessage);
      }
      setAcceptedRFQs([]);
      setRawAcceptedRFQs([]);
    } finally {
      // Always set loading to false when fetch completes, regardless of active tab
      setLoading(false);
    }
  };

  // Re-transform RFQ data when master data (currency and unit) becomes available
  useEffect(() => {
    // Only re-transform if master data is loaded and we have raw RFQ data
    // Use lengths instead of arrays to prevent infinite loops
    const currencyLoaded = !currencyLoading && currencyOptions && currencyOptions.length > 0;
    const unitLoaded = !uomLoading && attributeUnitOptions && attributeUnitOptions.length > 0;
    
    if (currencyLoaded && unitLoaded && rawRequestRFQs.length > 0) {
      // Check if current displayed values are IDs (need updating) or if requestRFQs is empty
      const needsUpdate = requestRFQs.length === 0 || 
        requestRFQs.some(rfq => {
          // Check if quantity contains a numeric unit (ID) instead of a label
          // Pattern: "78.00 3" where "3" is the unit ID
          const quantityMatch = rfq.quantity?.match(/\d+\.?\d*\s+(\d+)$/);
          if (quantityMatch && !isNaN(quantityMatch[1])) {
            // Check if this numeric value is actually a unit ID (not a valid unit label)
            const unitId = Number(quantityMatch[1]);
            const isUnitId = attributeUnitOptions.some(opt => Number(opt.value) === unitId);
            return isUnitId; // Only update if it's actually a unit ID
          }
          return false;
        });
      
      if (needsUpdate) {
        console.log('🔄 Re-transforming request RFQs with master data now available', {
          rawCount: rawRequestRFQs.length,
          currentCount: requestRFQs.length,
          currencyOptionsCount: currencyOptions.length,
          unitOptionsCount: attributeUnitOptions.length,
          sampleUnitFromRFQ: rawRequestRFQs[0]?.quantity_unit,
          sampleCurrencyFromRFQ: rawRequestRFQs[0]?.target_price_currency
        });
        
        // Always re-transform when master data is available to ensure correct mapping
        const transformedRFQs = rawRequestRFQs.map(rfq => {
          const transformed = transformRFQData(rfq);
          console.log('Transformed RFQ:', {
            rfqId: rfq.rfq_id,
            originalUnit: rfq.quantity_unit,
            transformedUnit: transformed.quantity,
            originalCurrency: rfq.target_price_currency,
            transformedPrice: transformed.expectedPrice
          });
          return transformed;
        });
        setRequestRFQs(transformedRFQs);
      }
      
      // Re-transform quoted RFQs - need to re-fetch to preserve quotation data
      // Only if we're on the quoted tab
      if (rawQuotedRFQs.length > 0 && activeTab === 'quoted') {
        // Check if we need to re-fetch (if quotedRFQs is empty or has IDs)
        const quotedNeedsUpdate = quotedRFQs.length === 0 || 
          quotedRFQs.some(rfq => {
            const quantityMatch = rfq.quantity?.match(/\d+\s+(\d+)$/);
            return quantityMatch && !isNaN(quantityMatch[1]);
          });
        
        if (quotedNeedsUpdate) {
          console.log('🔄 Re-transforming quoted RFQs with master data now available');
          // Re-fetch to ensure all data is correctly transformed
          fetchQuotedRFQs();
        }
      }
      
      // Re-transform rejected RFQs - need to re-fetch to preserve quotation data
      // Only if we're on the rejected tab
      if (rawRejectedRFQs.length > 0 && activeTab === 'rejected') {
        // Check if we need to re-fetch (if rejectedRFQs is empty or has IDs)
        const rejectedNeedsUpdate = rejectedRFQs.length === 0 || 
          rejectedRFQs.some(rfq => {
            const quantityMatch = rfq.quantity?.match(/\d+\s+(\d+)$/);
            return quantityMatch && !isNaN(quantityMatch[1]);
          });
        
        if (rejectedNeedsUpdate) {
          console.log('🔄 Re-transforming rejected RFQs with master data now available');
          // Re-fetch to ensure all data is correctly transformed
          fetchRejectedRFQs();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyLoading, uomLoading, currencyOptions?.length, attributeUnitOptions?.length, rawRequestRFQs.length, rawQuotedRFQs.length, rawRejectedRFQs.length, rawAcceptedRFQs.length, activeTab, requestRFQs.length, quotedRFQs.length, rejectedRFQs.length, acceptedRFQs.length]);

  // RFQ data structure - Request, Quoted, Rejected, and Accepted tabs fetch from database
  const rfqData = {
    // Request tab: RFQs matching seller's product categories that are NOT yet quoted by this seller
    request: requestRFQs,
    // Quoted tab: RFQs that have already been quoted by this seller
    quoted: quotedRFQs,
    // Rejected tab: RFQs that have been rejected by the buyer
    rejected: rejectedRFQs,
    // Accepted tab: RFQs that have been accepted by the buyer
    accepted: acceptedRFQs
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleSubmitQuotation = (rfqId) => {
    navigate('/submit-quotation-to-buyer', { state: { rfqId } });
  };

  const handleEditDraftQuotation = (rfq) => {
    const numericRfqId = rfq?.rfq_id || (typeof rfq?.id === 'string' ? parseInt(rfq.id.replace('REQ-', ''), 10) : rfq?.id);
    const quotationId = rfq?.quotationDbId || rfq?.quotationData?.quotation_id;
    navigate(`/submit-quotation-to-buyer?rfqId=${numericRfqId}&edit=true`, {
      state: {
        rfqId: numericRfqId ? `REQ-${numericRfqId}` : rfq?.id,
        quotationId,
        editMode: true,
      },
    });
  };

  const handleAskClarification = (rfqId) => {
    // Only check request tab data since other tabs are empty
    const rfq = rfqData.request.find(r => r.id === rfqId || r.rfq_id === rfqId);
    if (rfq) {
      setSelectedRFQ(rfq);
      setClarificationForm({
        subject: '',
        message: '',
        files: []
      });
      setShowClarificationModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowClarificationModal(false);
    setSelectedRFQ(null);
    setClarificationForm({
      subject: '',
      message: '',
      files: []
    });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setClarificationForm(prev => ({
      ...prev,
      files: [...prev.files, ...selectedFiles]
    }));
  };

  const handleRemoveFile = (index) => {
    setClarificationForm(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleFileClick = () => {
    clarificationFileInputRef.current?.click();
  };

  const handleSendClarification = async () => {
    if (!clarificationForm.subject || !clarificationForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!selectedRFQ || !selectedRFQ.rfq_id) {
      toast.error('RFQ information is missing');
      return;
    }

    try {
      // Step 1: Create clarification first to get clarification_id
      const clarificationData = {
        rfq_id: selectedRFQ.rfq_id,
        subject: clarificationForm.subject,
        message: clarificationForm.message,
        attachments: [] // Will be updated after file uploads
      };

      const createResponse = await CommonService.post('/rfq-clarification', clarificationData);
      
      if (!createResponse || !createResponse.success || !createResponse.data?.clarificationId) {
        throw new Error(createResponse?.message || 'Failed to create clarification');
      }

      const clarificationId = createResponse.data.clarificationId;

      // Step 2: Upload files with clarification_id if any
      const uploadedAttachments = [];
      if (clarificationForm.files && clarificationForm.files.length > 0) {
        for (const file of clarificationForm.files) {
          try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', 'clarification');
            formData.append('companyName', 'RFQ-Clarification');
            formData.append('fileType', 'clarification_attachment');
            formData.append('clarificationId', clarificationId);

            const uploadResponse = await CommonService.post('/upload/single', formData);
            if (uploadResponse && uploadResponse.success && uploadResponse.data) {
              const accessPath = uploadResponse.data.accessPath || uploadResponse.data.filePath || '';
              if (accessPath) {
                uploadedAttachments.push(accessPath);
              }
            }
          } catch (error) {
            console.error('Error uploading file:', error);
            toast.error(`Failed to upload ${file.name}`);
          }
        }

        // Step 3: Update clarification with attachment paths if files were uploaded
        if (uploadedAttachments.length > 0) {
          try {
            await CommonService.put(`/rfq-clarification/${clarificationId}/attachments`, {
              attachments: uploadedAttachments
            });
          } catch (error) {
            console.error('Error updating clarification with attachments:', error);
            // Don't fail the whole operation if attachment update fails
            toast.warning('Clarification sent, but some attachments may not have been saved');
          }
        }
      }

      toast.success('Clarification sent successfully');
      handleCloseModal();
    } catch (error) {
      console.error('Error sending clarification:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to send clarification';
      toast.error(errorMessage);
    }
  };

  const handleViewQuotation = async (rfqId) => {
    try {
      // Extract numeric RFQ ID from string like "REQ-4"
      const numericRfqId = typeof rfqId === 'string' ? parseInt(rfqId.replace('REQ-', ''), 10) : rfqId;
      
      // Helper function to find RFQ in an array
      const findRFQInArray = (array) => {
        return array.find(rfq => {
          const rfqIdNum = typeof rfq.rfq_id === 'string' ? parseInt(rfq.rfq_id, 10) : Number(rfq.rfq_id);
          return rfqIdNum === numericRfqId;
        });
      };
      
      // Find the RFQ in quotedRFQs, acceptedRFQs, or rejectedRFQs arrays
      let foundRFQ = findRFQInArray(quotedRFQs) 
        || findRFQInArray(acceptedRFQs) 
        || findRFQInArray(rejectedRFQs);

      if (!foundRFQ || !foundRFQ.quotationData) {
        toast.error('Quotation data not found');
        return;
      }

      const rfq = foundRFQ.originalData || foundRFQ;
      const quotation = foundRFQ.quotationData;

      // Parse payment terms if it's a JSON string
      let paymentTermsParsed = [];
      if (quotation.payment_terms) {
        try {
          paymentTermsParsed = typeof quotation.payment_terms === 'string' 
            ? JSON.parse(quotation.payment_terms) 
            : quotation.payment_terms;
        } catch (e) {
          console.warn('Error parsing payment_terms:', e);
          paymentTermsParsed = [];
        }
      }

      // Format quote date
      const quoteDate = quotation.quote_date 
        ? new Date(quotation.quote_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : quotation.created_at 
          ? new Date(quotation.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : 'N/A';

      // Format request date
      const requestDate = rfq.created_at 
        ? new Date(rfq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : 'N/A';

      // Get quotation currency
      const quotationCurrency = quotation.currency || rfq.target_price_currency || 'INR';
      const quotationCurrencyCode = getCurrencyLabel(quotationCurrency);
      const quotationCurrencySymbol = getCurrencySymbol(quotationCurrencyCode);

      // Calculate totals from line items
      const lineItems = quotation.items || [];
      const subtotal = lineItems.reduce((sum, item) => {
        const unitPrice = parseFloat(item.unit_price) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        return sum + (unitPrice * quantity);
      }, 0);

      const cgstTotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.cgst_amount) || 0);
      }, 0);

      const sgstTotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.sgst_amount) || 0);
      }, 0);

      const cessTotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.cess_amount) || 0);
      }, 0);

      const igstTotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.igst_amount) || 0);
      }, 0);

      const customsTotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.customs_amount) || 0);
      }, 0);

      const bcdTotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.bcd_amount) || 0);
      }, 0);

      const swsTotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.sws_amount) || 0);
      }, 0);

      const assessableValueTotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.assessable_value) || 0);
      }, 0);

      const taxTotal = lineItems.reduce((sum, item) => {
        const taxAmount = parseFloat(item.tax_amount) || 0;
        return sum + taxAmount;
      }, 0);

      const grandTotal = lineItems.reduce((sum, item) => {
        return sum + (parseFloat(item.total) || 0);
      }, 0);

      // Parse bulk pricing from all line items
      const bulkPricing = [];
      lineItems.forEach((item, itemIndex) => {
        if (item.bulk_pricing) {
          try {
            let itemBulkPricing = typeof item.bulk_pricing === 'string'
              ? JSON.parse(item.bulk_pricing)
              : item.bulk_pricing;
            
            // Ensure it's an array
            if (!Array.isArray(itemBulkPricing)) {
              itemBulkPricing = [];
            }
            
            // Add item number and description to each tier
            itemBulkPricing.forEach(tier => {
              bulkPricing.push({
                itemNumber: itemIndex + 1,
                itemDescription: item.description || `Item ${itemIndex + 1}`,
                minQty: tier.minQty || tier.min_qty || 0,
                maxQty: tier.maxQty || tier.max_qty || null,
                unitPrice: parseFloat(tier.unitPrice || tier.unit_price || 0),
                discount: parseFloat(tier.discount || 0)
              });
            });
          } catch (e) {
            console.warn(`Error parsing bulk_pricing for item ${itemIndex + 1}:`, e);
          }
        }
      });

      // Get payment terms details
      const advancePayment = quotation.advance_payment || (paymentTermsParsed.find(pt => pt.label === 'Advance Payment')?.value) || 'N/A';
      const balancePayment = quotation.balance_payment || (paymentTermsParsed.find(pt => pt.label === 'Balance Payment')?.value) || 'N/A';
      const paymentDueDays = quotation.payment_due_days ? `${quotation.payment_due_days} days` : 'N/A';

      // Get trade type
      const tradeType = quotation.trade_type || 'IN India to India - Intra-State (Same State)';
      const exportTaxType = quotation.export_tax_type || '';

      // Transform quotation data for display
      const transformedQuotation = {
        quotationId: quotation.quotation_number || `QUO-${quotation.quotation_id}`,
        quotationDbId: quotation.quotation_id,
        submittedOn: quotation.created_at 
          ? new Date(quotation.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : 'N/A',
        status: quotation.quote_status || 'Submitted',
        rfqId: rfqId,
        buyerName: rfq.buyer_name || rfq.company_name || 'Unknown Buyer',
        requestDate: requestDate,
        tradeType: tradeType,
        exportTaxType: exportTaxType,
        itemsRequested: {
          product: rfq.product_name || 'N/A',
          quantity: foundRFQ.requiredQuantity || foundRFQ.quantity || 'N/A',
          expectedPrice: foundRFQ.targetPriceRange || foundRFQ.expectedPrice || 'N/A'
        },
        quotationInfo: {
          quoteDate: quoteDate,
          validity: quotation.validity_days ? `${quotation.validity_days} days from quote date` : 'N/A'
        },
        lineItems: lineItems.map((item, index) => ({
          description: item.description || 'N/A',
          hsn: item.hsn_code || 'N/A',
          quantity: parseFloat(item.quantity) || 0,
          uom: item.uom || 'N/A',
          unitPrice: parseFloat(item.unit_price) || 0,
          discount: parseFloat(item.discount) || 0,
          cgst: parseFloat(item.cgst) || 0,
          sgst: parseFloat(item.sgst) || 0,
          igst: parseFloat(item.igst) || 0,
          cess: parseFloat(item.cess) || 0,
          tax: parseFloat(item.tax) || 0,
          customs: parseFloat(item.customs) || 0,
          customsAmount: parseFloat(item.customs_amount) || 0,
          bcdAmount: parseFloat(item.bcd_amount) || 0,
          swsAmount: parseFloat(item.sws_amount) || 0,
          assessableValue: parseFloat(item.assessable_value) || 0,
          igstAmount: parseFloat(item.igst_amount) || 0,
          cgstAmount: parseFloat(item.cgst_amount) || 0,
          sgstAmount: parseFloat(item.sgst_amount) || 0,
          cessAmount: parseFloat(item.cess_amount) || 0,
          total: parseFloat(item.total) || 0,
          countryOfOrigin: item.country_of_origin || 'N/A',
          warrantyPeriod: item.warranty_period || 'N/A',
          certifications: Array.isArray(item.certifications) ? item.certifications : 
            (typeof item.certifications === 'string' ? JSON.parse(item.certifications || '[]') : []),
          sampleAvailable: item.sample_offered || 'No',
          samplePrice: item.sample_cost ? formatCurrency(parseFloat(item.sample_cost), quotationCurrencyCode) : 'N/A',
          sampleLeadTime: item.dispatch_lead_time || 'N/A'
        })),
        totals: {
          subtotal: subtotal,
          cgst: cgstTotal,
          sgst: sgstTotal,
          igst: igstTotal,
          cess: cessTotal,
          customs: customsTotal,
          bcd: bcdTotal,
          sws: swsTotal,
          assessableValue: assessableValueTotal,
          tax: taxTotal,
          grandTotal: grandTotal
        },
        paymentTerms: {
          paymentDueWithin: paymentDueDays,
          advancePayment: advancePayment,
          balancePayment: balancePayment,
          incoterms: quotation.trade_terms || quotation.incoterms || 'N/A',
          deliveryTime: quotation.delivery_time || (quotation.lead_time_days ? `${quotation.lead_time_days} day(s) after initial payment` : 'N/A')
        },
        bulkPricing: bulkPricing,
        sizeDetails: lineItems.map((item, index) => {
          // Parse certifications for this item
          let certs = [];
          if (item.certifications) {
            if (Array.isArray(item.certifications)) {
              certs = item.certifications;
            } else if (typeof item.certifications === 'string') {
              try {
                certs = JSON.parse(item.certifications || '[]');
              } catch (e) {
                console.warn('Error parsing certifications:', e);
              }
            }
          }
          
          return {
            itemNumber: index + 1,
            description: item.description || 'N/A',
            hsnSacCode: item.hsn_code || 'N/A',
            countryOfOrigin: item.country_of_origin || 'N/A',
            warrantyPeriod: item.warranty_period || 'N/A',
            certifications: certs
          };
        }),
        sampleDetails: lineItems.map((item, index) => ({
          itemNumber: index + 1,
          description: item.description || 'N/A',
          sampleOffered: item.sample_offered || 'No',
          sampleType: item.sample_type || 'N/A',
          sampleQuantity: item.sample_quantity ? `${item.sample_quantity} ${item.sample_unit || ''}`.trim() : 'N/A',
          sampleCost: item.sample_cost ? formatCurrency(parseFloat(item.sample_cost), quotationCurrencyCode) : 'N/A',
          dispatchLeadTime: item.dispatch_lead_time ? `${item.dispatch_lead_time} day(s)` : 'N/A',
          returnPolicy: item.return_policy || 'N/A'
        })),
        supplierDescription: quotation.supplier_description || 'No description provided.',
        currency: quotationCurrencyCode,
        currencySymbol: quotationCurrencySymbol
      };

      setSelectedQuotation(transformedQuotation);
      setShowViewQuotationModal(true);
    } catch (error) {
      console.error('Error loading quotation:', error);
      toast.error('Failed to load quotation details');
    }
  };

  const handleCloseViewQuotationModal = () => {
    setShowViewQuotationModal(false);
    setSelectedQuotation(null);
  };

  const handleViewRequest = (rfqId) => {
    const rfq = [...rfqData.request, ...rfqData.quoted, ...rfqData.rejected, ...rfqData.accepted].find(r => r.id === rfqId);
    if (rfq) {
      setSelectedViewRequestRFQ(rfq);
      setShowViewRequestModal(true);
    }
  };

  const handleCloseViewRequestModal = () => {
    setShowViewRequestModal(false);
    setSelectedViewRequestRFQ(null);
  };

  const handleViewDetail = (rfqId, e) => {
    e?.stopPropagation();
    const rfq = [...rfqData.request, ...rfqData.quoted, ...rfqData.rejected].find(r => r.id === rfqId);
    if (rfq) {
      setSelectedRejectRFQ(rfq);
      setShowRejectReasonModal(true);
    }
  };

  const handleCloseRejectReasonModal = () => {
    setShowRejectReasonModal(false);
    setSelectedRejectRFQ(null);
  };

  const handleCloseAcceptanceDetailsModal = () => {
    setShowAcceptanceDetailsModal(false);
    setSelectedAcceptRFQ(null);
  };

  // Debug: Log data availability
  useEffect(() => {
    console.log('📋 Data Status:', {
      activeTab,
      requestRFQsCount: requestRFQs.length,
      quotedRFQsCount: quotedRFQs.length,
      rfqDataQuotedCount: rfqData.quoted.length,
      loading,
      quotedRFQsFetched
    });
  }, [activeTab, requestRFQs.length, quotedRFQs.length, loading, quotedRFQsFetched]);

  const filteredRFQs = rfqData[activeTab].filter(rfq => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      rfq.id.toLowerCase().includes(query) ||
      rfq.buyerName.toLowerCase().includes(query) ||
      rfq.products.toLowerCase().includes(query)
    );
  });

  return (
    <div className="buyer-rfq-list">
      <div className="brfq-main-content">
        <div className="brfq-title-section">
          <h1 className="brfq-main-title">Request for Quotation List</h1>
          <p className="brfq-subtitle">Manage incoming requests from buyers and submit your quotations.</p>
        </div>

        {/* Search Bar */}
        <div className="brfq-search-section">
          <div className="brfq-search-wrapper">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="brfq-search-icon"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              className="brfq-search-input"
              placeholder="Search by RFQ number, buyer name, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="brfq-tabs">
          <button
            className={`brfq-tab ${activeTab === 'request' ? 'brfq-tab-active' : ''}`}
            onClick={() => handleTabChange('request')}
          >
            Request ({rfqData.request.length})
          </button>
          <button
            className={`brfq-tab ${activeTab === 'quoted' ? 'brfq-tab-active' : ''}`}
            onClick={() => handleTabChange('quoted')}
          >
            Quoted ({rfqData.quoted.length})
          </button>
          <button
            className={`brfq-tab ${activeTab === 'accepted' ? 'brfq-tab-active' : ''}`}
            onClick={() => handleTabChange('accepted')}
          >
            Accepted Quote ({rfqData.accepted.length})
          </button>
          <button
            className={`brfq-tab ${activeTab === 'rejected' ? 'brfq-tab-active' : ''}`}
            onClick={() => handleTabChange('rejected')}
          >
            Rejected Quote ({rfqData.rejected.length})
          </button>
        </div>

        {/* RFQ Cards */}
        <div className="brfq-cards-container">
          {loading && (activeTab === 'request' || activeTab === 'quoted' || activeTab === 'rejected' || activeTab === 'accepted') ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: '15px', color: '#666' }}>Loading RFQ requests...</p>
            </div>
          ) : filteredRFQs.length > 0 ? (
            filteredRFQs.map((rfq) => (
              <div key={rfq.id} className="brfq-card">
                <div className="brfq-card-content">
                  <div className="brfq-card-header">
                    <div className="brfq-card-header-left">
                      <span className="brfq-rfq-id">{rfq.id}</span>
                      <span className={`brfq-status-badge ${getQuotationStatusClass(rfq.status)}`}>
                        {rfq.status}
                      </span>
                    </div>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="brfq-chevron-icon"
                    >
                      <path d="m9 18 6-6-6-6"></path>
                    </svg>
                  </div>

                  <div className="brfq-buyer-row">
                    <div className="brfq-buyer-info">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className="brfq-user-icon"
                      >
                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <span className="brfq-buyer-label">Buyer</span>
                      <span className="brfq-buyer-name">{rfq.buyerName}</span>
                    </div>
                    {activeTab === 'request' && (
                      <div className="brfq-action-buttons">
                        <button 
                          className="brfq-btn-submit"
                          onClick={() => handleSubmitQuotation(rfq.id)}
                        >
                          Submit Quotation
                        </button>
                        <button 
                          className="brfq-btn-clarification"
                          onClick={() => handleAskClarification(rfq.id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                          </svg>
                          Ask Clarification
                        </button>
                      </div>
                    )}
                    {activeTab === 'quoted' && (
                      <div className="brfq-action-buttons">
                        {rfq.isDraft && (
                          <button
                            className="brfq-btn-submit"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditDraftQuotation(rfq);
                            }}
                          >
                            Edit & Resubmit
                          </button>
                        )}
                        <button 
                          className="brfq-btn-clarification"
                          onClick={() => handleViewQuotation(rfq.id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                            <path d="M10 9H8"></path>
                            <path d="M16 13H8"></path>
                            <path d="M16 17H8"></path>
                          </svg>
                          View Quotation
                        </button>
                        <button 
                          className="brfq-btn-clarification"
                          onClick={() => handleViewRequest(rfq.id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                            <path d="M10 9H8"></path>
                            <path d="M16 13H8"></path>
                            <path d="M16 17H8"></path>
                          </svg>
                          View Request
                        </button>
                      </div>
                    )}
                    {activeTab === 'accepted' && (
                      <div className="brfq-action-buttons">
                        <button 
                          className="brfq-btn-clarification"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAcceptRFQ(rfq);
                            setShowAcceptanceDetailsModal(true);
                          }}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                            <path d="M10 9H8"></path>
                            <path d="M16 13H8"></path>
                            <path d="M16 17H8"></path>
                          </svg>
                          View Details
                        </button>
                        <button 
                          className="brfq-btn-clarification"
                          onClick={() => handleViewQuotation(rfq.id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                            <path d="M10 9H8"></path>
                            <path d="M16 13H8"></path>
                            <path d="M16 17H8"></path>
                          </svg>
                          View Quotation
                        </button>
                        <button 
                          className="brfq-btn-clarification"
                          onClick={() => handleViewRequest(rfq.id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                            <path d="M10 9H8"></path>
                            <path d="M16 13H8"></path>
                            <path d="M16 17H8"></path>
                          </svg>
                          View Request
                        </button>
                      </div>
                    )}
                    {activeTab === 'rejected' && (
                      <div className="brfq-action-buttons">
                        {rfq.rejectReason && (
                          <button 
                            className="brfq-btn-clarification"
                            onClick={(e) => handleViewDetail(rfq.id, e)}
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="14" 
                              height="14" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                              className="brfq-chat-icon"
                            >
                              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                              <path d="M10 9H8"></path>
                              <path d="M16 13H8"></path>
                              <path d="M16 17H8"></path>
                            </svg>
                            View Detail
                          </button>
                        )}
                        {!rfq.rejectReason && (
                          <button 
                            className="brfq-btn-clarification"
                            onClick={() => handleViewDetail(rfq.id)}
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="14" 
                              height="14" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                              className="brfq-chat-icon"
                            >
                              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                              <path d="M10 9H8"></path>
                              <path d="M16 13H8"></path>
                              <path d="M16 17H8"></path>
                            </svg>
                            View Detail
                          </button>
                        )}
                        <button 
                          className="brfq-btn-clarification"
                          onClick={() => handleViewRequest(rfq.id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="brfq-chat-icon"
                          >
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                            <path d="M10 9H8"></path>
                            <path d="M16 13H8"></path>
                            <path d="M16 17H8"></path>
                          </svg>
                          View Request
                        </button>
                      </div>
                    )}
                  </div>

                  <div className={`brfq-details-grid ${activeTab === 'quoted' ? 'brfq-details-grid-quoted' : ''} ${activeTab === 'rejected' ? 'brfq-details-grid-rejected' : ''} ${activeTab === 'accepted' ? 'brfq-details-grid-accepted' : ''}`}>
                  <div className="brfq-detail-item">
                    <div className="brfq-detail-label">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className="brfq-detail-icon"
                      >
                        <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                        <path d="M12 22V12"></path>
                        <polyline points="3.29 7 12 12 20.71 7"></polyline>
                        <path d="m7.5 4.27 9 5.15"></path>
                      </svg>
                      Products
                    </div>
                    <div className="brfq-detail-value">{rfq.products}</div>
                  </div>

                  <div className="brfq-detail-item">
                    <div className="brfq-detail-label">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className="brfq-detail-icon"
                      >
                        <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                        <path d="M12 22V12"></path>
                        <polyline points="3.29 7 12 12 20.71 7"></polyline>
                        <path d="m7.5 4.27 9 5.15"></path>
                      </svg>
                      Category
                    </div>
                    <div className="brfq-detail-value">{rfq.category}</div>
                  </div>

                  <div className="brfq-detail-item">
                    <div className="brfq-detail-label">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className="brfq-detail-icon"
                      >
                        <line x1="4" x2="20" y1="9" y2="9"></line>
                        <line x1="4" x2="20" y1="15" y2="15"></line>
                        <line x1="10" x2="8" y1="3" y2="21"></line>
                        <line x1="16" x2="14" y1="3" y2="21"></line>
                      </svg>
                      Quantity
                    </div>
                    <div className="brfq-detail-value">{rfq.quantity}</div>
                  </div>

                  <div className="brfq-detail-item">
                    <div className="brfq-detail-label">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className="brfq-detail-icon"
                      >
                        <path d="M6 3h12"></path>
                        <path d="M6 8h12"></path>
                        <path d="m6 13 8.5 8"></path>
                        <path d="M6 13h3"></path>
                        <path d="M9 13c6.667 0 6.667-10 0-10"></path>
                      </svg>
                       Target Price
                    </div>
                    <div className="brfq-detail-value brfq-price-value">{rfq.expectedPrice}</div>
                  </div>

                  {((activeTab === 'quoted' || activeTab === 'rejected' || activeTab === 'accepted') && rfq.quotedPrice) && (
                    <div className="brfq-detail-item">
                      <div className="brfq-detail-label">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="brfq-detail-icon"
                        >
                          <line x1="12" x2="12" y1="2" y2="22"></line>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                        Quoted Price
                      </div>
                      <div className="brfq-detail-value brfq-price-value brfq-quoted-price-value">{rfq.quotedPrice}</div>
                    </div>
                  )}

                  {activeTab === 'accepted' && (
                    <div className="brfq-detail-item">
                      <div className="brfq-detail-label">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="brfq-detail-icon"
                        >
                          <path d="M8 2v4"></path>
                          <path d="M16 2v4"></path>
                          <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                          <path d="M3 10h18"></path>
                        </svg>
                        Accepted On
                      </div>
                      <div className="brfq-detail-value">
                        {rfq.acceptedOn || rfq.submittedOn}
                      </div>
                    </div>
                  )}

                  {(activeTab === 'quoted' || activeTab === 'rejected') && (
                    <div className="brfq-detail-item">
                      <div className="brfq-detail-label">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="brfq-detail-icon"
                        >
                          <path d="M8 2v4"></path>
                          <path d="M16 2v4"></path>
                          <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                          <path d="M3 10h18"></path>
                        </svg>
                        {activeTab === 'quoted' ? 'Quoted On' : 'Rejected On'}
                      </div>
                      <div className="brfq-detail-value">
                        {activeTab === 'quoted'
                          ? rfq.quotedOn || rfq.submittedOn
                          : rfq.rejectedOn || rfq.submittedOn}
                      </div>
                    </div>
                  )}

                  {activeTab === 'rejected' && rfq.rejectReason && (
                    <div className="brfq-detail-item brfq-reject-reason-item">
                      <div className="brfq-detail-label">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="brfq-detail-icon"
                        >
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                          <path d="M9 10h6"></path>
                        </svg>
                        Reject Reason
                      </div>
                      <div className="brfq-detail-value brfq-reject-reason-value">
                        {formatRejectionReason(rfq.rejectReason)}
                      </div>
                    </div>
                  )}
                </div>
                </div>
              </div>
            ))
          ) : (
            <div className="brfq-empty-state">
              <p>No RFQs found in this category.</p>
            </div>
          )}
        </div>
      </div>

      {/* Ask Clarification Modal */}
      {showClarificationModal && selectedRFQ && (
        <div className="brfq-modal-overlay" onClick={handleCloseModal}>
          <div className="brfq-modal" onClick={(e) => e.stopPropagation()}>
            <div className="brfq-modal-header">
              <div className="brfq-modal-header-left">
                <div className="brfq-modal-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <div>
                  <h2 className="brfq-modal-title">Ask Clarification</h2>
                  <p className="brfq-modal-subtitle">Send a clarification request to the buyer about their RFQ.</p>
                </div>
              </div>
              <button className="brfq-modal-close" onClick={handleCloseModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="brfq-modal-body">
              {/* RFQ Details Section */}
              <div className="brfq-order-summary">
                <div className="brfq-summary-item">
                  <span className="brfq-summary-label">RFQ Number:</span>
                  <span className="brfq-summary-value">{selectedRFQ.id}</span>
                </div>
                <div className="brfq-summary-item">
                  <span className="brfq-summary-label">Buyer:</span>
                  <span className="brfq-summary-value">{selectedRFQ.buyerName}</span>
                </div>
                <div className="brfq-summary-item">
                  <span className="brfq-summary-label">Product:</span>
                  <span className="brfq-summary-value">{selectedRFQ.products}</span>
                </div>
                <div className="brfq-summary-item">
                  <span className="brfq-summary-label">Quantity:</span>
                  <span className="brfq-summary-value">{selectedRFQ.quantity}</span>
                </div>
                <div className="brfq-summary-item">
                  <span className="brfq-summary-label">Submitted:</span>
                  <span className="brfq-summary-value">{selectedRFQ.submittedOn}</span>
                </div>
              </div>

              {/* Clarification Information */}
              <div className="brfq-order-info">
                <div className="brfq-form-group">
                  <label className="brfq-form-label">Subject <span className="brfq-required">*</span></label>
                  <input
                    type="text"
                    className="brfq-form-input"
                    placeholder="e.g., Clarification needed on product specifications"
                    value={clarificationForm.subject}
                    onChange={(e) => setClarificationForm(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>

                <div className="brfq-form-group">
                  <label className="brfq-form-label">Message <span className="brfq-required">*</span></label>
                  <textarea
                    className="brfq-form-textarea"
                    placeholder="Please provide detailed information about..."
                    rows="3"
                    value={clarificationForm.message}
                    onChange={(e) => setClarificationForm(prev => ({ ...prev, message: e.target.value }))}
                  />
                  <p className="brfq-form-note">The buyer will receive your clarification request via email and can respond directly.</p>
                </div>

                <div className="brfq-form-group">
                  <label className="brfq-form-label">Attachment (Optional)</label>
                  <div className="brfq-file-upload">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-file-icon">
                      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                      <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                      <path d="M10 9H8"></path>
                      <path d="M16 13H8"></path>
                      <path d="M16 17H8"></path>
                    </svg>
                    <input
                      type="file"
                      ref={clarificationFileInputRef}
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                      multiple
                    />
                    <button type="button" className="brfq-file-btn" onClick={handleFileClick}>Choose Files</button>
                    <span className="brfq-file-text">
                      {clarificationForm.files.length > 0 
                        ? `${clarificationForm.files.length} file(s) selected`
                        : 'Upload supporting documents (optional)'}
                    </span>
                  </div>
                  <p className="brfq-form-note">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF (Max 10MB per file)</p>
                  {clarificationForm.files.length > 0 && (
                    <div className="brfq-file-list">
                      {clarificationForm.files.map((file, index) => (
                        <div key={index} className="brfq-file-item">
                          <span className="brfq-file-name">{file.name}</span>
                          <button 
                            type="button"
                            className="brfq-file-remove"
                            onClick={() => handleRemoveFile(index)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="brfq-modal-footer">
              <button className="brfq-modal-btn brfq-modal-btn-cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button 
                type="button"
                className="brfq-modal-btn brfq-modal-btn-confirm" 
                onClick={handleSendClarification}
                disabled={!clarificationForm.subject || !clarificationForm.message}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                Send Clarification
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Request Modal */}
      {showViewRequestModal && selectedViewRequestRFQ && (
        <div className="brfq-modal-overlay" onClick={handleCloseViewRequestModal}>
          <div className="brfq-view-request-modal" onClick={(e) => e.stopPropagation()}>
            {/* Sticky Header */}
            <div className="brfq-view-request-header">
              <div>
                <h2 className="brfq-view-request-title">Request Details</h2>
                <p className="brfq-view-request-subtitle">View complete information about this request for quotation</p>
              </div>
              <button className="brfq-view-request-close" onClick={handleCloseViewRequestModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="brfq-view-request-body">
              {/* Request Summary */}
              <div className="brfq-view-request-summary">
                <div className="brfq-view-request-summary-item">
                  <span className="brfq-view-request-summary-label">Request ID</span>
                  <p className="brfq-view-request-summary-value">{selectedViewRequestRFQ.id}</p>
                </div>
                <div className="brfq-view-request-summary-item">
                  <span className="brfq-view-request-summary-label">Buyer Name</span>
                  <p className="brfq-view-request-summary-value">{selectedViewRequestRFQ.buyerName}</p>
                </div>
                <div className="brfq-view-request-summary-item">
                  <span className="brfq-view-request-summary-label">Requested On</span>
                  <p className="brfq-view-request-summary-value">{selectedViewRequestRFQ.requestedOn || selectedViewRequestRFQ.submittedOn}</p>
                </div>
              </div>

              {/* Product Details Section */}
              <div className="brfq-view-request-section">
                <div className="brfq-view-request-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                    <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                    <path d="M12 22V12"></path>
                    <polyline points="3.29 7 12 12 20.71 7"></polyline>
                    <path d="m7.5 4.27 9 5.15"></path>
                  </svg>
                  <h3 className="brfq-view-request-section-title">Product Details</h3>
                </div>
                <div className="brfq-view-request-grid">
                  <div className="brfq-view-request-grid-row-4">
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Product Name</label>
                      <p className="brfq-view-request-field-value">{selectedViewRequestRFQ.productName || selectedViewRequestRFQ.products}</p>
                    </div>
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Target Price Range</label>
                      <p className="brfq-view-request-field-value brfq-view-request-price">
                        {selectedViewRequestRFQ.targetPriceRange || selectedViewRequestRFQ.expectedPrice || 'N/A'}
                      </p>
                    </div>
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Required Quantity</label>
                      <p className="brfq-view-request-field-value">
                        {selectedViewRequestRFQ.requiredQuantity || selectedViewRequestRFQ.quantity || 'N/A'}
                      </p>
                    </div>
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Procurement Method</label>
                      <p className="brfq-view-request-field-value">
                        {formatProcurementMethod(selectedViewRequestRFQ.procurementMethod)}
                      </p>
                    </div>
                  </div>
                  <div className="brfq-view-request-grid-row-2">
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Product Category</label>
                      <div className="brfq-view-request-badges">
                        {(selectedViewRequestRFQ.productCategories || [selectedViewRequestRFQ.category]).map((cat, idx) => (
                          <span key={idx} className="brfq-view-request-badge">{cat}</span>
                        ))}
                      </div>
                    </div>
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Keywords</label>
                      <div className="brfq-view-request-badges">
                        {(selectedViewRequestRFQ.keywords || [selectedViewRequestRFQ.products]).map((keyword, idx) => (
                          <span key={idx} className="brfq-view-request-badge brfq-view-request-badge-outline">{keyword}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="brfq-view-request-field brfq-view-request-field-full">
                    <label className="brfq-view-request-field-label">Product Description</label>
                    <p className="brfq-view-request-description">{selectedViewRequestRFQ.productDescription || 'The buyer has not provided a detailed product description. Please refer to the product name, category, and specifications mentioned above for more information about the requested item.'}</p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="brfq-view-request-divider"></div>

              {/* Payment & Shipping Details */}
              <div className="brfq-view-request-section">
                <div className="brfq-view-request-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path>
                    <path d="M15 18H9"></path>
                    <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path>
                    <circle cx="17" cy="18" r="2"></circle>
                    <circle cx="7" cy="18" r="2"></circle>
                  </svg>
                  <h3 className="brfq-view-request-section-title">Payment & Shipping Details</h3>
                </div>
                <div className="brfq-view-request-grid">
                  <div className="brfq-view-request-grid-row-4">
                    <div className="brfq-view-request-field">
                      <div className="brfq-view-request-field-label-with-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect width="20" height="14" x="2" y="5" rx="2"></rect>
                          <line x1="2" x2="22" y1="10" y2="10"></line>
                        </svg>
                        <label className="brfq-view-request-field-label">Payment Terms</label>
                      </div>
                      <p className="brfq-view-request-field-value">{selectedViewRequestRFQ.paymentTerms || '30% advance, 70% on delivery'}</p>
                    </div>
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Incoterms</label>
                      <p className="brfq-view-request-field-value">{selectedViewRequestRFQ.incoterms || 'FOB'}</p>
                    </div>
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Expected Delivery</label>
                      <p className="brfq-view-request-field-value">{selectedViewRequestRFQ.expectedDelivery || 'Not specified'}</p>
                    </div>
                    <div className="brfq-view-request-field">
                      <label className="brfq-view-request-field-label">Preferred Shipping Method</label>
                      <p className="brfq-view-request-field-value">{selectedViewRequestRFQ.preferredShippingMethod || 'Road'}</p>
                    </div>
                  </div>
                  <div className="brfq-view-request-field brfq-view-request-field-full">
                    <div className="brfq-view-request-field-label-with-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      <label className="brfq-view-request-field-label">Shipping Address</label>
                    </div>
                    <p className="brfq-view-request-field-value">
                      {selectedViewRequestRFQ.shippingAddress || 'Address not specified'}
                      {selectedViewRequestRFQ.shippingCity && `, ${selectedViewRequestRFQ.shippingCity}`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="brfq-view-request-divider"></div>

              {/* Special Requirements */}
              <div className="brfq-view-request-section">
                <div className="brfq-view-request-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <path d="M14 2v6h6"></path>
                    <path d="M16 13H8"></path>
                    <path d="M16 17H8"></path>
                    <path d="M10 9H8"></path>
                  </svg>
                  <h3 className="brfq-view-request-section-title">Special Requirements & Notes</h3>
                </div>
                <div className="brfq-view-request-grid">
                  <div className="brfq-view-request-field brfq-view-request-field-full">
                    <p className="brfq-view-request-description">{selectedViewRequestRFQ.specialRequirements || 'The buyer has not specified any special requirements or additional notes for this request. Standard specifications and terms apply.'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Quotation Modal */}
      {showViewQuotationModal && selectedQuotation && (
        <div className="brfq-modal-overlay" onClick={handleCloseViewQuotationModal}>
          <div className="brfq-view-quotation-modal" onClick={(e) => e.stopPropagation()}>
            {/* Sticky Header */}
            <div className="brfq-view-quotation-header">
              <div>
                <h2 className="brfq-view-quotation-title">Seller Quotation Response</h2>
                <p className="brfq-view-quotation-subtitle">View complete quotation details submitted by the seller</p>
              </div>
              <button className="brfq-view-quotation-close" onClick={handleCloseViewQuotationModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="brfq-view-quotation-body">
              {/* RFQ Details Section */}
              <div className="brfq-view-quotation-section">
                <div className="brfq-view-quotation-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-quotation-section-icon">
                    <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                    <path d="M12 22V12"></path>
                    <polyline points="3.29 7 12 12 20.71 7"></polyline>
                    <path d="m7.5 4.27 9 5.15"></path>
                  </svg>
                  <h3 className="brfq-view-quotation-section-title">RFQ Details</h3>
                </div>
                <div className="brfq-view-quotation-rfq-summary">
                  <div className="brfq-view-quotation-rfq-item">
                    <span className="brfq-view-quotation-rfq-label">Buyer's Request ID</span>
                    <p className="brfq-view-quotation-rfq-value">{selectedQuotation.rfqId}</p>
                  </div>
                  <div className="brfq-view-quotation-rfq-item">
                    <span className="brfq-view-quotation-rfq-label">Buyer Name</span>
                    <p className="brfq-view-quotation-rfq-value">{selectedQuotation.buyerName}</p>
                  </div>
                  <div className="brfq-view-quotation-rfq-item">
                    <span className="brfq-view-quotation-rfq-label">Request Date</span>
                    <p className="brfq-view-quotation-rfq-value">{selectedQuotation.requestDate}</p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="brfq-view-quotation-divider"></div>

              {/* Items Requested */}
              <div className="brfq-view-quotation-section">
                <div className="brfq-view-quotation-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-quotation-section-icon">
                    <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                    <path d="M12 22V12"></path>
                    <polyline points="3.29 7 12 12 20.71 7"></polyline>
                    <path d="m7.5 4.27 9 5.15"></path>
                  </svg>
                  <h3 className="brfq-view-quotation-section-title">Items Requested</h3>
                </div>
                <div className="brfq-view-quotation-rfq-summary">
                  <div className="brfq-view-quotation-rfq-item">
                    <span className="brfq-view-quotation-rfq-label">RFQ Details</span>
                    <p className="brfq-view-quotation-rfq-value">{selectedQuotation.itemsRequested.product}</p>
                  </div>
                  <div className="brfq-view-quotation-rfq-item">
                    <span className="brfq-view-quotation-rfq-label">Quantity</span>
                    <p className="brfq-view-quotation-rfq-value">{selectedQuotation.itemsRequested.quantity}</p>
                  </div>
                  <div className="brfq-view-quotation-rfq-item">
                    <span className="brfq-view-quotation-rfq-label">Expected Target Price</span>
                    <p className="brfq-view-quotation-rfq-value brfq-view-quotation-price">{selectedQuotation.itemsRequested.expectedPrice}</p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="brfq-view-quotation-divider"></div>

              {/* Quotation Information */}
              <div className="brfq-view-quotation-section">
                <div className="brfq-view-quotation-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-quotation-section-icon">
                    <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"></path>
                    <path d="M14 2v5a1 1 0 0 0 1 1h5"></path>
                    <path d="M10 9H8"></path>
                    <path d="M16 13H8"></path>
                    <path d="M16 17H8"></path>
                  </svg>
                  <h3 className="brfq-view-quotation-section-title">Quotation Information</h3>
                </div>
                <div className="brfq-view-quotation-grid brfq-view-quotation-grid-4">
                  <div className="brfq-view-quotation-field">
                    <label className="brfq-view-quotation-field-label">Quotation ID</label>
                    <p className="brfq-view-quotation-field-value">{selectedQuotation.quotationId}</p>
                  </div>
                  <div className="brfq-view-quotation-field">
                    <label className="brfq-view-quotation-field-label">Quote Status</label>
                    <span className="brfq-view-quotation-status-badge-inline">{selectedQuotation.status}</span>
                  </div>
                  <div className="brfq-view-quotation-field">
                    <label className="brfq-view-quotation-field-label">Quote Date</label>
                    <p className="brfq-view-quotation-field-value">{selectedQuotation.quotationInfo.quoteDate}</p>
                  </div>
                  <div className="brfq-view-quotation-field">
                    <label className="brfq-view-quotation-field-label">Validity (Days)</label>
                    <p className="brfq-view-quotation-field-value">{selectedQuotation.quotationInfo.validity}</p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="brfq-view-quotation-divider"></div>

              {/* Line Items */}
              <div className="brfq-view-quotation-section">
                <div className="brfq-view-quotation-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-quotation-section-icon">
                    <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"></path>
                    <path d="M14 2v5a1 1 0 0 0 1 1h5"></path>
                    <path d="M10 9H8"></path>
                    <path d="M16 13H8"></path>
                    <path d="M16 17H8"></path>
                  </svg>
                  <h3 className="brfq-view-quotation-section-title">Line Items</h3>
                </div>
                <div className="brfq-view-quotation-table-wrapper">
                  <table className="brfq-view-quotation-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th>HSN</th>
                        <th>Qty</th>
                        <th>UOM</th>
                        <th>Unit Price</th>
                        <th>Disc %</th>
                        {/* Conditional columns based on trade type */}
                        {selectedQuotation.tradeType === 'IN India to India - Intra-State (Same State)' && (
                          <>
                        <th>CGST %</th>
                        <th>SGST %</th>
                        <th>CESS %</th>
                          </>
                        )}
                        {selectedQuotation.tradeType === 'IN India to India - Inter-State (Different States)' && (
                          <>
                            <th>IGST %</th>
                            <th>CESS %</th>
                          </>
                        )}
                        {selectedQuotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                          selectedQuotation.exportTaxType !== 'LUT (Letter of Undertaking) - 0% IGST' && (
                          <>
                            <th>IGST %</th>
                            <th>CESS %</th>
                          </>
                        )}
                        {selectedQuotation.tradeType === '🌎 →IN Import into India (Foreign → India)' && (
                          <>
                            <th>Customs %</th>
                            <th>IGST %</th>
                            <th>CESS %</th>
                            <th>BCD</th>
                            <th>SWS</th>
                          </>
                        )}
                        {selectedQuotation.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' && (
                          <th>Tax %</th>
                        )}
                        <th className="brfq-view-quotation-th-total">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuotation.lineItems.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.description}</td>
                          <td>{item.hsn}</td>
                          <td>{item.quantity}</td>
                          <td>{item.uom}</td>
                          <td>{formatCurrency(item.unitPrice, selectedQuotation.currency)}</td>
                          <td>{item.discount}%</td>
                          {/* Conditional cells based on trade type */}
                          {selectedQuotation.tradeType === 'IN India to India - Intra-State (Same State)' && (
                            <>
                          <td>{item.cgst}%</td>
                          <td>{item.sgst}%</td>
                          <td>{item.cess}%</td>
                            </>
                          )}
                          {selectedQuotation.tradeType === 'IN India to India - Inter-State (Different States)' && (
                            <>
                              <td>{item.igst}%</td>
                              <td>{item.cess}%</td>
                            </>
                          )}
                          {selectedQuotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                            selectedQuotation.exportTaxType !== 'LUT (Letter of Undertaking) - 0% IGST' && (
                            <>
                              <td>{item.igst}%</td>
                              <td>{item.cess}%</td>
                            </>
                          )}
                          {selectedQuotation.tradeType === '🌎 →IN Import into India (Foreign → India)' && (
                            <>
                              <td>{item.customs}%</td>
                              <td>{item.igst}%</td>
                              <td>{item.cess}%</td>
                              <td>{formatCurrency(item.bcdAmount, selectedQuotation.currency)}</td>
                              <td>{formatCurrency(item.swsAmount, selectedQuotation.currency)}</td>
                            </>
                          )}
                          {selectedQuotation.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' && (
                            <td>{item.tax}%</td>
                          )}
                          <td className="po-table-col-total-cell">{formatCurrency(item.total, selectedQuotation.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="po-preview-total-row">
                        <td></td>
                        <td className="po-preview-summary-label">Subtotal (Qty × Unit Price):</td>
                        <td colSpan={
                          selectedQuotation.tradeType === 'IN India to India - Intra-State (Same State)' ? 8 :
                          selectedQuotation.tradeType === 'IN India to India - Inter-State (Different States)' ? 7 :
                          selectedQuotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                          selectedQuotation.exportTaxType !== 'LUT (Letter of Undertaking) - 0% IGST' ? 7 :
                          selectedQuotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                          selectedQuotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST' ? 5 :
                          selectedQuotation.tradeType === '🌎 →IN Import into India (Foreign → India)' ? 10 :
                          selectedQuotation.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' ? 6 : 8
                        }></td>
                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.subtotal, selectedQuotation.currency)}</span></td>
                      </tr>
                      
                      {/* Intra-State: Show CGST, SGST, CESS */}
                      {selectedQuotation.tradeType === 'IN India to India - Intra-State (Same State)' && (
                        <>
                      <tr className="po-preview-total-row">
                        <td></td>
                        <td className="po-preview-summary-label">CGST:</td>
                        <td colSpan="8"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.cgst, selectedQuotation.currency)}</span></td>
                      </tr>
                      <tr className="po-preview-total-row">
                        <td></td>
                        <td className="po-preview-summary-label">SGST:</td>
                        <td colSpan="8"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.sgst, selectedQuotation.currency)}</span></td>
                      </tr>
                      <tr className="po-preview-total-row">
                        <td></td>
                        <td className="po-preview-summary-label">CESS:</td>
                        <td colSpan="8"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.cess, selectedQuotation.currency)}</span></td>
                      </tr>
                        </>
                      )}
                      
                      {/* Inter-State: Show IGST, CESS */}
                      {selectedQuotation.tradeType === 'IN India to India - Inter-State (Different States)' && (
                        <>
                          <tr className="po-preview-total-row">
                            <td></td>
                            <td className="po-preview-summary-label">IGST:</td>
                            <td colSpan="7"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.igst, selectedQuotation.currency)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td></td>
                            <td className="po-preview-summary-label">CESS:</td>
                            <td colSpan="7"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.cess, selectedQuotation.currency)}</span></td>
                          </tr>
                        </>
                      )}
                      
                      {/* Export: Show IGST, CESS (if not LUT) */}
                      {selectedQuotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                        selectedQuotation.exportTaxType !== 'LUT (Letter of Undertaking) - 0% IGST' && (
                        <>
                          <tr className="po-preview-total-row">
                            <td></td>
                            <td className="po-preview-summary-label">IGST:</td>
                            <td colSpan="7"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.igst, selectedQuotation.currency)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td></td>
                            <td className="po-preview-summary-label">CESS:</td>
                            <td colSpan="7"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.cess, selectedQuotation.currency)}</span></td>
                          </tr>
                        </>
                      )}
                      
                      {/* Import: Show Customs, IGST, CESS, BCD, SWS */}
                      {selectedQuotation.tradeType === '🌎 →IN Import into India (Foreign → India)' && (
                        <>
                          <tr className="po-preview-total-row">
                            <td></td>
                            <td className="po-preview-summary-label">Customs Duty:</td>
                            <td colSpan="10"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.customs, selectedQuotation.currency)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td></td>
                            <td className="po-preview-summary-label">Assessable Value:</td>
                            <td colSpan="10"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.assessableValue, selectedQuotation.currency)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td></td>
                            <td className="po-preview-summary-label">BCD (Basic Customs Duty):</td>
                            <td colSpan="10"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.bcd, selectedQuotation.currency)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td></td>
                            <td className="po-preview-summary-label">SWS (Social Welfare Surcharge):</td>
                            <td colSpan="10"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.sws, selectedQuotation.currency)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td></td>
                            <td className="po-preview-summary-label">IGST:</td>
                            <td colSpan="10"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.igst, selectedQuotation.currency)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td></td>
                            <td className="po-preview-summary-label">CESS:</td>
                            <td colSpan="10"></td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.cess, selectedQuotation.currency)}</span></td>
                          </tr>
                        </>
                      )}
                      
                      {/* Third Country Trade: Show Tax */}
                      {selectedQuotation.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' && (
                        <tr className="po-preview-total-row">
                          <td></td>
                          <td className="po-preview-summary-label">Tax:</td>
                          <td colSpan="6"></td>
                          <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.tax, selectedQuotation.currency)}</span></td>
                        </tr>
                      )}
                      
                      <tr className="po-preview-total-row po-preview-grand-total">
                        <td></td>
                        <td className="po-preview-summary-label"><span className="font-semibold">Grand Total (Sum of Line Items):</span></td>
                        <td colSpan={
                          selectedQuotation.tradeType === 'IN India to India - Intra-State (Same State)' ? 8 :
                          selectedQuotation.tradeType === 'IN India to India - Inter-State (Different States)' ? 7 :
                          selectedQuotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                          selectedQuotation.exportTaxType !== 'LUT (Letter of Undertaking) - 0% IGST' ? 7 :
                          selectedQuotation.tradeType === 'IN → 🌎 Export from India (India → Foreign)' && 
                          selectedQuotation.exportTaxType === 'LUT (Letter of Undertaking) - 0% IGST' ? 5 :
                          selectedQuotation.tradeType === '🌎 →IN Import into India (Foreign → India)' ? 10 :
                          selectedQuotation.tradeType === '🌎 Third Country Trade (Foreign → Foreign)' ? 6 : 8
                        }></td>
                        <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(selectedQuotation.totals.grandTotal, selectedQuotation.currency)}</span></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Divider */}
              <div className="brfq-view-quotation-divider"></div>

              {/* Payment & Delivery Terms */}
              <div className="brfq-view-quotation-section">
                <div className="brfq-view-quotation-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-quotation-section-icon">
                    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                    <path d="M10 6h4"></path>
                    <path d="M10 10h4"></path>
                    <path d="M10 14h4"></path>
                    <path d="M10 18h4"></path>
                  </svg>
                  <h3 className="brfq-view-quotation-section-title">Payment & Delivery Terms</h3>
                </div>
                <div className="brfq-view-quotation-grid brfq-view-quotation-grid-3">
                  <div className="brfq-view-quotation-field">
                    <div className="brfq-view-quotation-field-label-with-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 2v4"></path>
                        <path d="M16 2v4"></path>
                        <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                        <path d="M3 10h18"></path>
                      </svg>
                      <label className="brfq-view-quotation-field-label">Payment Due Within</label>
                    </div>
                    <p className="brfq-view-quotation-field-value">{selectedQuotation.paymentTerms.paymentDueWithin}</p>
                  </div>
                  <div className="brfq-view-quotation-field">
                    <div className="brfq-view-quotation-field-label-with-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" x2="12" y1="2" y2="22"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                      <label className="brfq-view-quotation-field-label">Advance Payment (%)</label>
                    </div>
                    <p className="brfq-view-quotation-field-value">{selectedQuotation.paymentTerms.advancePayment}</p>
                  </div>
                  <div className="brfq-view-quotation-field">
                    <div className="brfq-view-quotation-field-label-with-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" x2="12" y1="2" y2="22"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                      <label className="brfq-view-quotation-field-label">Balance Payment (%)</label>
                    </div>
                    <p className="brfq-view-quotation-field-value">{selectedQuotation.paymentTerms.balancePayment}</p>
                  </div>
                  <div className="brfq-view-quotation-field">
                    <label className="brfq-view-quotation-field-label">Incoterms</label>
                    <p className="brfq-view-quotation-field-value">{selectedQuotation.paymentTerms.incoterms}</p>
                  </div>
                  <div className="brfq-view-quotation-field">
                    <div className="brfq-view-quotation-field-label-with-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path>
                        <path d="M15 18H9"></path>
                        <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path>
                        <circle cx="17" cy="18" r="2"></circle>
                        <circle cx="7" cy="18" r="2"></circle>
                      </svg>
                      <label className="brfq-view-quotation-field-label">Delivery Time</label>
                    </div>
                    <p className="brfq-view-quotation-field-value">{selectedQuotation.paymentTerms.deliveryTime}</p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="brfq-view-quotation-divider"></div>

              {/* Bulk Pricing */}
              <div className="brfq-view-quotation-section">
                <div className="brfq-view-quotation-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-quotation-section-icon">
                    <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"></path>
                    <circle cx="7.5" cy="7.5" r=".5" fill="currentColor"></circle>
                  </svg>
                  <h3 className="brfq-view-quotation-section-title">Bulk Pricing (Discount)</h3>
                </div>
                <div className="brfq-view-quotation-table-wrapper">
                  <table className="brfq-view-quotation-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th className="brfq-view-quotation-th-left">Item Description</th>
                        <th className="brfq-view-quotation-th-left">Min Qty</th>
                        <th className="brfq-view-quotation-th-left">Max Qty</th>
                        <th className="brfq-view-quotation-th-left">Unit Price</th>
                        <th className="brfq-view-quotation-th-left">Discount %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuotation.bulkPricing && selectedQuotation.bulkPricing.length > 0 ? (
                        selectedQuotation.bulkPricing.map((tier, index) => (
                        <tr key={index}>
                            <td>{tier.itemNumber}</td>
                            <td className="brfq-view-quotation-td-left">{tier.itemDescription}</td>
                          <td className="brfq-view-quotation-td-left">{tier.minQty}</td>
                          <td className="brfq-view-quotation-td-left">{tier.maxQty || 'Unlimited'}</td>
                            <td className="brfq-view-quotation-td-left">{formatCurrency(tier.unitPrice, selectedQuotation.currency)}</td>
                          <td className="brfq-view-quotation-td-left">
                            <span className={`brfq-view-quotation-discount-badge ${tier.discount > 0 ? 'brfq-view-quotation-discount-active' : ''}`}>
                              {tier.discount}%
                            </span>
                          </td>
                        </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                            No bulk pricing available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Divider */}
              <div className="brfq-view-quotation-divider"></div>

              {/* Size Details & Customization */}
              <div className="brfq-view-quotation-section">
                <div className="brfq-view-quotation-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-quotation-section-icon">
                    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                    <path d="m9 12 2 2 4-4"></path>
                  </svg>
                  <h3 className="brfq-view-quotation-section-title">Size Details & Customization Information</h3>
                </div>
                <div className="brfq-view-quotation-table-wrapper">
                  <table className="brfq-view-quotation-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th>HSN/SAC Code</th>
                        <th>Country of Origin</th>
                        <th>Warranty Period</th>
                        <th>Certifications/Standards Met</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuotation.sizeDetails && Array.isArray(selectedQuotation.sizeDetails) && selectedQuotation.sizeDetails.length > 0 ? (
                        selectedQuotation.sizeDetails.map((detail, index) => (
                          <tr key={index}>
                            <td>{detail.itemNumber}</td>
                            <td>{detail.description}</td>
                            <td>{detail.hsnSacCode}</td>
                            <td>{detail.countryOfOrigin}</td>
                            <td>{detail.warrantyPeriod}</td>
                            <td>
                              {detail.certifications && detail.certifications.length > 0 ? (
                                <span>{detail.certifications.join(', ')}</span>
                              ) : (
                                <span style={{ color: '#999' }}>N/A</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                            No size details available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Divider */}
              <div className="brfq-view-quotation-divider"></div>

              {/* Sample Details */}
              <div className="brfq-view-quotation-section">
                <div className="brfq-view-quotation-section-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-quotation-section-icon">
                    <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                    <path d="M12 22V12"></path>
                    <polyline points="3.29 7 12 12 20.71 7"></polyline>
                    <path d="m7.5 4.27 9 5.15"></path>
                  </svg>
                  <h3 className="brfq-view-quotation-section-title">Sample Details</h3>
                </div>
                <div className="brfq-view-quotation-table-wrapper">
                  <table className="brfq-view-quotation-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th>Sample Offered</th>
                        <th>Sample Type</th>
                        <th>Sample Quantity</th>
                        <th>Sample Cost</th>
                        <th>Dispatch Lead Time</th>
                        <th>Return Policy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuotation.sampleDetails && Array.isArray(selectedQuotation.sampleDetails) && selectedQuotation.sampleDetails.length > 0 ? (
                        selectedQuotation.sampleDetails.map((detail, index) => (
                          <tr key={index}>
                            <td>{detail.itemNumber}</td>
                            <td>{detail.description}</td>
                            <td>
                              {detail.sampleOffered === 'Yes' ? (
                                <span className="brfq-view-quotation-sample-yes-text">{detail.sampleOffered}</span>
                              ) : (
                                detail.sampleOffered
                              )}
                            </td>
                            <td>{detail.sampleType}</td>
                            <td>{detail.sampleQuantity}</td>
                            <td>{detail.sampleCost}</td>
                            <td>{detail.dispatchLeadTime}</td>
                            <td>{detail.returnPolicy}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                            No sample details available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>


            </div>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {showRejectReasonModal && selectedRejectRFQ && (
        <div className="brfq-modal-overlay" onClick={handleCloseRejectReasonModal}>
          <div className="brfq-view-request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="brfq-view-request-header">
              <div>
                <h2 className="brfq-view-request-title">Reject Reason</h2>
                <p className="brfq-view-request-subtitle">View rejection details for this RFQ</p>
              </div>
              <button className="brfq-view-request-close" onClick={handleCloseRejectReasonModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>

            <div className="brfq-view-request-body">
              {(() => {
                // Get rejection details from transformed data or quotationData
                const rejectReason = selectedRejectRFQ.rejectReason || 
                                   selectedRejectRFQ.quotationData?.rejection_reason || 
                                   null;
                const rejectComment = selectedRejectRFQ.rejectComment || 
                                     selectedRejectRFQ.quotationData?.rejection_remarks || 
                                     null;
                const rejectAttachments = selectedRejectRFQ.rejectAttachments || 
                                         (selectedRejectRFQ.quotationData?.rejection_attachment ? [{
                                           name: selectedRejectRFQ.quotationData.rejection_attachment.split('/').pop() || 'Rejection_Attachment',
                                           url: selectedRejectRFQ.quotationData.rejection_attachment.startsWith('http') 
                                             ? selectedRejectRFQ.quotationData.rejection_attachment 
                                             : `http://localhost:3000${selectedRejectRFQ.quotationData.rejection_attachment.startsWith('/') ? selectedRejectRFQ.quotationData.rejection_attachment : '/' + selectedRejectRFQ.quotationData.rejection_attachment}`,
                                           type: selectedRejectRFQ.quotationData.rejection_attachment.split('.').pop()?.toUpperCase() || 'FILE',
                                           size: 'N/A',
                                           uploadedDate: selectedRejectRFQ.quotationData.rejected_at 
                                             ? new Date(selectedRejectRFQ.quotationData.rejected_at).toISOString().split('T')[0] 
                                             : 'N/A'
                                         }] : []);
                
                return (
                  <>
                    {rejectReason && rejectReason !== 'N/A' && (
                <div className="brfq-view-request-section">
                  <div className="brfq-view-request-section-header">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <path d="M14 2v6h6"></path>
                      <path d="M16 13H8"></path>
                      <path d="M16 17H8"></path>
                      <path d="M10 9H8"></path>
                    </svg>
                    <h3 className="brfq-view-request-section-title">Reason</h3>
                  </div>
                  <div className="brfq-view-request-grid">
                    <div className="brfq-view-request-field brfq-view-request-field-full">
                            <p className="brfq-view-request-description">{formatRejectionReason(rejectReason)}</p>
                    </div>
                  </div>
                </div>
              )}

                    {rejectComment && rejectComment !== 'N/A' && (
                <div className="brfq-view-request-section">
                  <div className="brfq-view-request-section-header">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                      <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <h3 className="brfq-view-request-section-title">Comment</h3>
                  </div>
                  <div className="brfq-view-request-grid">
                    <div className="brfq-view-request-field brfq-view-request-field-full">
                            <p className="brfq-view-request-description">{rejectComment}</p>
                    </div>
                  </div>
                </div>
              )}

                    {!rejectReason && !rejectComment && (
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <p className="brfq-view-request-description" style={{ color: '#999', fontStyle: 'italic' }}>
                              No rejection details available for this quotation.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {rejectAttachments && rejectAttachments.length > 0 && (
                <div className="brfq-view-request-section">
                  <div className="brfq-view-request-section-header">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                      <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                    </svg>
                    <h3 className="brfq-view-request-section-title">Attachments</h3>
                  </div>
                  <div className="brfq-view-request-grid">
                    <div className="brfq-view-request-field brfq-view-request-field-full">
                      <div className="brfq-reject-attachments-list">
                              {rejectAttachments.map((attachment, index) => (
                          <div key={index} className="brfq-reject-attachment-item">
                            <div className="brfq-attachment-left">
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="20" 
                                height="20" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                className="brfq-attachment-icon"
                              >
                                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                                <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                                <path d="M10 9H8"></path>
                                <path d="M16 13H8"></path>
                                <path d="M16 17H8"></path>
                              </svg>
                              <div className="brfq-attachment-info">
                                <div className="brfq-attachment-name">{attachment.name}</div>
                                <div className="brfq-attachment-details">
                                  <span>{attachment.type || 'PDF'}</span>
                                  <span className="brfq-attachment-separator">•</span>
                                  <span>{attachment.size || '2.4 MB'}</span>
                                  <span className="brfq-attachment-separator">•</span>
                                  <span>Uploaded: {attachment.uploadedDate || '2025-11-01'}</span>
                                </div>
                              </div>
                            </div>
                            <div className="brfq-attachment-actions">
                              <button 
                                className="brfq-attachment-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.open(attachment.url, '_blank');
                                }}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                  <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                View
                              </button>
                              <button 
                                className="brfq-attachment-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.open(attachment.url, '_blank');
                                }}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="7 10 12 15 17 10"></polyline>
                                  <line x1="12" x2="12" y1="15" y2="3"></line>
                                </svg>
                                Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Acceptance Details Modal */}
      {showAcceptanceDetailsModal && selectedAcceptRFQ && (
        <div className="brfq-modal-overlay" onClick={handleCloseAcceptanceDetailsModal}>
          <div className="brfq-view-request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="brfq-view-request-header">
              <div>
                <h2 className="brfq-view-request-title">Acceptance Details</h2>
                <p className="brfq-view-request-subtitle">View acceptance details for this quotation</p>
              </div>
              <button className="brfq-view-request-close" onClick={handleCloseAcceptanceDetailsModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>

            <div className="brfq-view-request-body">
              {(() => {
                // Get acceptance notes from transformed data or quotationData
                const acceptanceNotes = selectedAcceptRFQ.acceptanceNotes || 
                                     selectedAcceptRFQ.quotationData?.acceptance_notes || 
                                     null;
                
                // Get acceptance attachment from transformed data or quotationData
                const acceptanceAttachment = selectedAcceptRFQ.acceptanceAttachment || 
                                           selectedAcceptRFQ.quotationData?.acceptance_attachment || 
                                           null;
                
                return (
                  <>
                    {acceptanceNotes && (
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-section-header">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                            <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                          </svg>
                          <h3 className="brfq-view-request-section-title">Comment</h3>
                        </div>
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <p className="brfq-view-request-description">{acceptanceNotes}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {acceptanceAttachment && (
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-section-header">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                          </svg>
                          <h3 className="brfq-view-request-section-title">Attachments</h3>
                        </div>
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <div className="brfq-reject-attachments-list">
                              <div className="brfq-reject-attachment-item">
                                <div className="brfq-attachment-left">
                                  <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="brfq-attachment-icon"
                                  >
                                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                                    <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                                    <path d="M10 9H8"></path>
                                    <path d="M16 13H8"></path>
                                    <path d="M16 17H8"></path>
                                  </svg>
                                  <div className="brfq-attachment-info">
                                    <div className="brfq-attachment-name">
                                      {acceptanceAttachment.split('/').pop() || 'Acceptance Attachment'}
                                    </div>
                                    <div className="brfq-attachment-details">
                                      <span>{acceptanceAttachment.split('.').pop()?.toUpperCase() || 'FILE'}</span>
                                      <span className="brfq-attachment-separator">•</span>
                                      <span>Attachment</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="brfq-attachment-actions">
                                  <button 
                                    className="brfq-attachment-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const baseUrl = 'http://localhost:3000';
                                      const fileUrl = acceptanceAttachment.startsWith('http') ? acceptanceAttachment : `${baseUrl}${acceptanceAttachment.startsWith('/') ? acceptanceAttachment : '/' + acceptanceAttachment}`;
                                      window.open(fileUrl, '_blank');
                                    }}
                                  >
                                    <svg 
                                      xmlns="http://www.w3.org/2000/svg" 
                                      width="16" 
                                      height="16" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2" 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round"
                                    >
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                      <circle cx="12" cy="12" r="3"></circle>
                                    </svg>
                                    View
                                  </button>
                                  <button 
                                    className="brfq-attachment-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const baseUrl = 'http://localhost:3000';
                                      const fileUrl = acceptanceAttachment.startsWith('http') ? acceptanceAttachment : `${baseUrl}${acceptanceAttachment.startsWith('/') ? acceptanceAttachment : '/' + acceptanceAttachment}`;
                                      const link = document.createElement('a');
                                      link.href = fileUrl;
                                      link.download = acceptanceAttachment.split('/').pop() || 'download';
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <svg 
                                      xmlns="http://www.w3.org/2000/svg" 
                                      width="16" 
                                      height="16" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2" 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round"
                                    >
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                      <polyline points="7 10 12 15 17 10"></polyline>
                                      <line x1="12" x2="12" y1="15" y2="3"></line>
                                    </svg>
                                    Download
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!acceptanceNotes && !acceptanceAttachment && (
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <p className="brfq-view-request-description" style={{ color: '#999', fontStyle: 'italic' }}>
                              No acceptance details available for this quotation.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

